import { Formatters } from '../shared/lib/formatters';
import { LoadingSpinner } from '../shared/components';
import { emptyState } from '../shared/components/Narrative';
import { icon } from '../shared/components/icons';
// R115 C1: @mentions (shared with server/tasks.js — one parser for picker, render and notify)
import { renderWithMentions, mentionQuery, filterPeople, insertMention, keepPicked, mentionLabel, mentionRoster } from '../../../lib/taskMentions.mjs';
// R115 fix (review tasks 0): saves send only the changed keys; a newer server copy folds into the open draft.
import { patchBody, mergeDraft, replaySave, changedKeys } from '../../../lib/taskPatch.mjs';
// R120 (Madel: "sorted by due date by default"): one comparator for List, Board and My Work + the Sort select.
import { sortTasks, compareTasks, normSortMode, SORT_MODES, SORT_LABELS, type TaskSortMode } from '../../../lib/taskSort.mjs';
// R120 (contract C8): which cleaner-roster row this login works as — a stale link falls back to the active email twin.
import { resolveRosterId } from '../../../lib/rosterIdentity.mjs';

// Tasks (slice 1) — the portfolio task manager (API: /api/tasks).
//
// Two views:
//   MY WORK   the daily driver for field roles — my open tasks sorted by due
//             date, done/canceled collapsed below; cleaners ALSO see today's
//             turnovers as read-only context cards (managed in Housekeeping,
//             never duplicated here).
//   LIST      the office view — filter bar + KPI band computed from the
//             FILTERED set, drawn either as dense rows (List) or as a kanban
//             board (Board: To do / In progress / Blocked / Done columns, drag a
//             card between columns to change its status). The layout choice is
//             remembered per browser (localStorage 'tk_layout').
// Shared: right-side task panel (title inline-editable, status/priority
// segmented controls, description + checklist + comments on the left, a
// Details card on the right, sticky Save/Cancel/Delete footer) and the
// '+ New task' modal with the same structure.
//
// All panel/modal form inputs are STATE-BACKED (mirrored to a draft object on
// input) so redraws never lose edits. 'Today' is always localToday()
// (en-CA local date) — never toISOString(), which flips the day after ~5pm
// Pacific. Role comes from sessionStorage 'userRole' (set by /api/me — never a
// direct profiles read, per the RLS-recursion rule).
//
// R111 restyled the chrome onto the main.css HostBuddy tokens; R112 (Jesse:
// "redesign the tasks board as well, as well as the individual tasks") is the
// layout pass: board layout, avatar discs / due pills / progress rings on rows
// and cards, the two-column panel, the searchable assignee picker. Every
// data-* / id / datalist / sessionStorage contract from R111 is kept.
//
// R115 C1/C2: '@' in a comment or description opens a mention picker (the
// person is notified in the app — TopNav bell); comments render @mentions;
// the Activity card is a timeline of EVERY change (server-side before/after
// diff, lib/taskDiff.js); watchers get an editor in Details; every write
// dispatches 'tasks:changed' so the bell refreshes, and the bell's
// 'tasks:focus' opens a task when this page is already on screen.
//
// R120 (Madel: "the activity log should show every change, not just status —
// and sort by due date"): List / Board / My Work sort due-first
// (lib/taskSort.mjs; a Sort select remembers the choice per browser,
// localStorage 'tk_sort'); the Activity card reads in plain words ("Due date
// Oct 19 → Oct 20", "Assigned Kai · removed Lacey", the checklist item
// ticked, the description before/after behind "Show change", the starting
// values on "Created the task"), filters Changes | Comments | All (default
// Changes, 'tk_log_filter'), opens fresh on every task and says where the
// detailed history starts; closing the panel with unsaved edits asks
// "Save changes before closing?" (Cancel stays an explicit discard).

const API = '/api/tasks';
const OPEN_STATUSES = ['todo', 'in_progress', 'blocked'];
const STATUSES: Array<[string, string]> = [
  ['todo', 'To do'],
  ['in_progress', 'In progress'],
  ['blocked', 'Blocked'],
  ['done', 'Done'],
  ['canceled', 'Canceled'],
];
// Board columns (canceled tasks live in the Done column, struck through).
const BOARD_COLS: Array<[string, string]> = [
  ['todo', 'To do'],
  ['in_progress', 'In progress'],
  ['blocked', 'Blocked'],
  ['done', 'Done'],
];
const BOARD_DONE_DAYS = 14;      // the Done column shows the last two weeks (older ones stay in the list)
const BOARD_DONE_CAP = 40;
// One placed column: `items` are the cards on screen, `more` the Done overflow kept in the row list.
// R115 fix 2/3 (verifier D0): one drawer save, captured when it is queued.
// want0 = the draft as the user left it; base0 = the capture of the save just
// ahead of it (same task + panel opening, still pending), else the panel's
// base — the same layer as want0, so want0 vs base0 is its own edits only;
// layer = the panel's base (a pure server copy); dep = that pending save
// (kept until this one lands — lib/taskPatch.mjs replaySave carries the ones
// that failed); landed = the server took it (or there was nothing to send);
// done = finished either way.
type SaveJob = { id: string; want0: any; base0: any; layer: any; epoch: number; silent: boolean; dep: SaveJob | null; landed: boolean; done: boolean };
type BoardColumn = { key: string; label: string; items: any[]; more: number; sub: string; pool?: any[] };   // pool: the column's full set before the display cap (KPIs count it)
const PRIORITIES: Array<[string, string]> = [
  ['urgent', 'Urgent'],
  ['high', 'High'],
  ['normal', 'Normal'],
  ['low', 'Low'],
];
// R111: priority renders as a main.css .pill (was a colored dot) — urgent red, high amber, the rest quiet gray.
const PRIORITY_PILL: Record<string, string> = { urgent: 'pill-red', high: 'pill-amber', normal: 'pill-gray', low: 'pill-gray' };
const FIELD_ROLES = ['cleaner', 'inspector', 'maintenance'];
const LAYOUT_KEY = 'tk_layout';   // localStorage: 'list' | 'board'
const SORT_KEY = 'tk_sort';       // R120 localStorage: lib/taskSort.mjs SORT_MODES
const LOG_FILTER_KEY = 'tk_log_filter';   // R120 localStorage: 'changes' | 'comments' | 'all'
type LogFilter = 'changes' | 'comments' | 'all';
const LOG_FILTERS: Array<[LogFilter, string]> = [['changes', 'Changes'], ['comments', 'Comments'], ['all', 'All']];
// R120: field-level history (lib/taskDiff R115 C2) went live at this moment —
// a task created before it only ever logged created + status changes.
const LOG_FULL_SINCE = '2026-10-16T05:11:40Z';
// R120: who wrote an automatic line ('system' + its source).
const SYSTEM_WHO: Record<string, string> = { reservation_sync: 'Reservation sync', mtr_tour: 'MTR tour sync', pds_flag: 'PDS flag', dedupe: 'Issues cleanup', issues_board: 'Issues board' };
// R120: the unsaved-changes prompt names the fields in plain words.
const FIELD_WORD: Record<string, string> = { title: 'title', description: 'description', status: 'status', priority: 'priority', due_date: 'due date', due_time: 'due time', assignees: 'assignees', assignee: 'assignees', watchers: 'watchers', listing_id: 'property', listing_name: 'property', link: 'link', checklist: 'checklist', blocked_by: 'blocked by', recurrence: 'repeat', template_id: 'template' };
const AV_TONES = 6;               // .tk-av.t0 … .t5

export class TasksPage {
  private container: HTMLElement;

  // data
  private tasks: any[] | null = null;      // GET /  (null = fetch failed)
  private dateChanges: any[] = [];         // R88 #4.1: recent reservation date changes (sync-written feed)
  private dcOpen = false;
  private people: any[] = [];              // GET /assignees
  private mentionPeople: any[] = [];       // R115 fix (cross 4): staff only — what @mentions offer/resolve (no owner / accounting / vendor logins)
  private listings: any[] = [];            // GET /listings
  private myTurns: any[] = [];             // cleaner-only: today's ao_turns (read-only context)

  // view + list filters
  private view: 'my' | 'list' = 'list';
  private layout: 'list' | 'board' = 'list';   // R112: list rows or kanban board (persisted)
  private fStatus = 'open';
  private fAssignee = 'all';
  private fPriority = 'all';
  private fListing = 'all';
  private fSearch = '';
  private searchFocus = false;             // restore search-box focus after redraw
  private dragId: string | null = null;    // R112: board card in flight (HTML5 drag)

  // detail panel — draft is the single source of truth for every form input,
  // so a redraw mid-edit never loses keystrokes (hard requirement).
  private drawerId: string | null = null;
  private draft: any = null;
  // R115 fix (review tasks 0): the server copy the draft started from (reset
  // from every echo). A save POSTs only the keys where draft ≠ draftBase —
  // never the whole (possibly stale) task — and drawer saves run one at a
  // time so each diffs against the previous save's echo.
  private draftBase: any = null;
  private saveQ: Promise<void> = Promise.resolve();
  // R115 fix 2 (verifier D0): a save is captured when it is QUEUED (task id +
  // the draft as the user left it + the base those edits are relative to) and
  // never re-read from this.draft when it runs, so closing the panel or
  // opening another task can't drop it. drawerEpoch = which opening of the
  // panel a save came from (a reopened panel is seeded afresh from the list);
  // serverCopy = per task, the newest copy any server answer carried — what a
  // queued save rebases on; goneIds = tasks that answered 404 / were deleted
  // here (their queued saves stop — the user was told once).
  private drawerEpoch = 0;
  private serverCopy = new Map<string, any>();
  private goneIds = new Set<string>();
  private lastJob = new Map<string, SaveJob>();   // per task: the newest queued save (a later one chains on it while it is pending)
  private panelLanded: any = null;                // R115 fix 3: the capture of the newest save of THIS panel opening that landed since the panel was last re-based (rebasePanel)
  private deleting = new Set<string>();           // R115 fix 3: tasks whose DELETE (from this page) is in flight — a save's 404 then is expected, not news
  // R115 fix 3: per task, the keys of a save whose outcome is unknown (the
  // connection dropped — the server may have run it) and the copy it was sent
  // on. Until an answer brings a newer copy, every save of that task re-sends
  // those keys GUARDED (base_updated_at): it lands only on exactly that copy,
  // else 409 brings the truth and the save is replayed on it.
  private unsure = new Map<string, { keys: string[]; at: string }>();
  private ckText = '';                     // new-checklist-item input (state-backed)
  // R104 #8: expanded checklist items (description / instructions / sub-items) + per-item "add sub-item" drafts
  private ckOpen = new Set<string>();
  private ckSubText: Record<string, string> = {};
  private commentText = '';                // comment composer (state-backed)
  private commentMentions: string[] = [];  // R115 C1: emails picked from the @ picker for the unsent comment
  private wsQuery = '';                    // R115: watchers picker search (state-backed like asQuery)
  private logAll = false;                  // R115 C2: activity timeline shows the newest 20 until "Show all"
  private logClosed = false;               // R115 C2: the user collapsed the Activity card (R120: per opening — reset by openDrawer)
  private logFilter: LogFilter = 'changes';   // R120 (D32): Changes | Comments | All — comments already have their own card
  private sortMode: TaskSortMode = 'due';     // R120 (D33): List / Board order (lib/taskSort.mjs)
  private closeAsk = false;                // R120: "Save changes before closing?" is showing over the panel
  private commentFiles: File[] = []; // comment photos (state-backed — survives drawer redraws)
  private searchCaret = 0;                 // caret position to restore after a filter redraw
  // R112: searchable assignee picker query (lives here so a redraw keeps it; the menu's open flag is focus-scoped in the binder)
  private asQuery = '';

  // create modal
  private showCreate = false;
  private cDraft: any = null;
  // R104 #7 (Jesse: "search for the property when creating a task"): type-ahead
  // (ResolutionsPage idiom) — the datalist shows LABELS; a label match resolves
  // listing_id; non-matching text clears the selection and blocks save so a
  // name never silently maps to another home.
  private listingTyped = '';    // drawer
  private listingBad = false;
  private cListingTyped = '';   // create modal
  private cListingBad = false;
  private cAsQuery = '';

  private note = '';

  constructor(container: HTMLElement) {
    this.container = container;
    try { const v = localStorage.getItem(LAYOUT_KEY); if (v === 'board' || v === 'list') this.layout = v; } catch { /* private mode / blocked storage */ }
    try { this.sortMode = normSortMode(localStorage.getItem(SORT_KEY)); } catch { /* private mode — due-first */ }
    try { const f = localStorage.getItem(LOG_FILTER_KEY); if (f === 'changes' || f === 'comments' || f === 'all') this.logFilter = f; } catch { /* private mode — Changes */ }
    // R115 C1: a bell click while this page is on screen (app:navigate to the
    // same page is a no-op) — open the task now; a task newer than this list
    // reloads the page, whose render() takes the 'tk_focus_task' hand-off.
    document.addEventListener('tasks:focus', (e) => {
      const id = String((e as CustomEvent).detail || '');
      const body = document.getElementById('tk-body');
      if (!id || !body || !this.container.contains(body)) return;
      if (this.taskById(id)) {
        try { sessionStorage.removeItem('tk_focus_task'); } catch { /* private mode */ }
        this.openDrawer(id);
      } else void this.render();
    });
  }
  // R115 C1: every successful task write — the bell re-reads ~1.5 s later
  // (the server pushes notifications after it answers).
  private static changed(): void { document.dispatchEvent(new CustomEvent('tasks:changed')); }

  async render(): Promise<void> {
    this.container.innerHTML = `<div class="page-content"><div id="tk-body"><div style="padding:40px; text-align:center;">${LoadingSpinner.render()}</div></div></div>`;
    const role = TasksPage.fieldRole();
    this.view = FIELD_ROLES.includes(role) ? 'my' : 'list';
    const [t, a, l, dc] = await Promise.all([
      fetch(`${API}`).then(r => r.ok ? r.json() : null).catch(() => null),
      fetch(`${API}/assignees`).then(r => r.ok ? r.json() : null).catch(() => null),
      fetch(`${API}/listings`).then(r => r.ok ? r.json() : null).catch(() => null),
      fetch(`${API}/date-changes`).then(r => r.ok ? r.json() : null).catch(() => null),
    ]);
    this.dateChanges = (dc && Array.isArray(dc.changes)) ? dc.changes : [];
    this.tasks = t ? (t.tasks || []) : null;
    this.people = (a && a.people) || [];
    this.mentionPeople = mentionRoster(this.people);
    this.listings = (l && l.listings) || [];
    // Cleaners also get today's turnovers as read-only cards. Best-effort —
    // absence of the assetops module must never break this page.
    if (role === 'cleaner') { try { await this.loadCleanerTurns(); } catch (_) { this.myTurns = []; } }
    this.draw();
    // R108: the Issues board's "✓ Task" chip opens that task here.
    let focus = '';
    try { focus = sessionStorage.getItem('tk_focus_task') || ''; sessionStorage.removeItem('tk_focus_task'); } catch { /* private mode */ }
    if (focus && this.taskById(focus)) this.openDrawer(focus);
    // persistent instance: a drawer left open before navigating away must be re-seeded from the
    // fresh list (or closed if the task is gone) so Save never writes a stale clone back
    else if (this.drawerId) { if (this.taskById(this.drawerId)) this.openDrawer(this.drawerId); else this.closeDrawer(); }
  }

  // ── identity / role helpers (HousekeepingPage idioms) ─────────
  private static fieldRole(): string { try { return sessionStorage.getItem('userRole') || ''; } catch { return ''; } }
  private static profileEmail(): string { try { const p = JSON.parse(sessionStorage.getItem('userProfile') || '{}'); return String(p.email || '').trim(); } catch { return ''; } }
  private isField(): boolean { return FIELD_ROLES.includes(TasksPage.fieldRole()); }
  // Office = admin/director/manager/remote_ops or unknown (server re-checks on DELETE).
  private isOffice(): boolean { return !this.isField(); }

  // ── fetch helpers (MaintenancePage.ts pattern) ────────────────
  private async get(path: string): Promise<any> {
    const r = await fetch(`${API}${path}`);
    const j = await r.json().catch(() => ({}));
    if (!r.ok) { alert(j.error || `request failed (${r.status})`); return null; }
    return j;
  }
  private async post(path: string, body: any): Promise<any> {
    // R115 fix 3 verify: a dropped connection used to throw out of postStatus /
    // moveTask (no alert, an unhandled rejection, a card left in the wrong column).
    // Now it alerts by task name and returns null, so callers revert as on a 4xx.
    let r: { ok: boolean; status: number; j: any };
    try { r = await this.postRaw(path, body); }
    catch {
      const t = body && body.id ? this.taskById(String(body.id)) : null;
      alert(`No connection — ${t ? `"${t.title || 'this task'}"` : 'the change'} was not saved. Try again.`);
      return null;
    }
    if (!r.ok) alert(r.j.error || `request failed (${r.status})`);
    return r.ok ? r.j : null;
  }
  // The answer whatever the status (the drawer save handles a 409 itself).
  private async postRaw(path: string, body: any): Promise<{ ok: boolean; status: number; j: any }> {
    const r = await fetch(`${API}${path}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
    const j = await r.json().catch(() => ({}));
    if (r.ok) TasksPage.changed();
    return { ok: r.ok, status: r.status, j: j || {} };
  }
  private async del(path: string): Promise<boolean> {
    const r = await fetch(`${API}${path}`, { method: 'DELETE' });
    if (!r.ok) { const j = await r.json().catch(() => ({} as any)); alert(j.error || `request failed (${r.status})`); }
    if (r.ok) TasksPage.changed();
    return r.ok;
  }

  // ── formatting helpers ────────────────────────────────────────
  private esc(s: any): string { return Formatters.escapeHtml(s == null ? '' : String(s)); }
  // "Today" in the USER'S timezone (en-CA = YYYY-MM-DD). toISOString() is UTC,
  // which from ~5pm Pacific labels TOMORROW as today — wrong day for field work.
  private localToday(): string {
    try { return new Date().toLocaleDateString('en-CA'); } catch { return new Date().toISOString().split('T')[0]; }
  }
  private daysAgoStr(n: number): string {
    const d = new Date(this.localToday() + 'T00:00:00Z');
    d.setUTCDate(d.getUTCDate() - n);
    return d.toISOString().slice(0, 10);
  }
  private fmtDate(iso: string | null): string {
    if (!iso) return '—';
    const d = new Date(iso + 'T00:00:00Z');
    if (isNaN(d.getTime())) return '—';
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
  }
  private fmtAt(iso: string | null): string {
    if (!iso) return '—';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '—';
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) + ' ' +
      d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  }
  // R104 #10: the 15-min timer never wipes an open panel / half-typed task /
  // a card mid-drag (App.ts asks before refreshing; the hub keeps this instance for that).
  suspendAutoRefresh(): boolean {
    return !!(this.drawerId || this.showCreate || this.commentText || this.commentFiles.length || this.dragId);
  }
  // R104 #7: unique label per listing (dupes get "(id)") — /api/tasks/listings
  // already collapses short_name into name, so read .name only.
  private static normLabel(v: any): string { return String(v ?? '').trim().toLowerCase(); }
  private listingLabelFor(l: any): string {
    const base = String(l.name || l.id).trim();
    const dupes = this.listings.filter(x => TasksPage.normLabel(x.name || x.id) === TasksPage.normLabel(base));   // review R104 #23: case/space-insensitive, like the lookup
    return dupes.length > 1 ? `${base} (${l.id})` : base;
  }
  private listingByLabel(typed: string): any | null {
    const q = TasksPage.normLabel(typed);
    return q ? (this.listings.find(l => TasksPage.normLabel(this.listingLabelFor(l)) === q) || null) : null;
  }
  private listingDatalist(id: string): string {
    return `<datalist id="${id}">${this.listings.map(l => this.listingLabelFor(l)).sort((a, b) => a.localeCompare(b)).map(lb => `<option value="${this.esc(lb)}"></option>`).join('')}</datalist>`;
  }
  // The error span stays a SIBLING of the input (bindListingTypeahead toggles it via inp.parentElement).
  private listingInput(attr: string, dlId: string, typed: string, bad: boolean): string {
    return `<input type="text" class="input" ${attr} list="${dlId}" autocomplete="off" value="${this.esc(typed)}" placeholder="Search properties…" title="Type to search ${this.listings.length} properties">${this.listingDatalist(dlId)}<span class="tk-field-err" data-listing-err style="display:${bad ? '' : 'none'};">No matching property — pick from the list or clear the field.</span>`;
  }
  private priorityLabel(p: string): string {
    const m = PRIORITIES.find(([k]) => k === p);
    return m ? m[1] : (p || 'Normal');
  }
  private priorityPill(p: string | null | undefined): string {
    const key = String(p || 'normal');
    return `<span class="pill ${PRIORITY_PILL[key] || 'pill-gray'}" title="Priority: ${this.esc(this.priorityLabel(key))}">${this.esc(this.priorityLabel(key))}</span>`;
  }
  // Type-ahead binder: mirror the text on every keystroke (no redraw — keeps
  // focus); resolve label→id on 'change' (datalist pick / blur) and on an
  // exact 'input' match so a pick resolves without waiting for blur.
  private bindListingTypeahead(scope: HTMLElement, attr: string, getDraft: () => any, setTyped: (v: string) => void, setBad: (b: boolean) => void): void {
    const inp = scope.querySelector<HTMLInputElement>(`[${attr}]`);
    if (!inp) return;
    // review R104 #22: no redraw on 'change' — a redraw inside the blur stole
    // focus (bindCreate refocuses Title) and dropped the click that caused it.
    // The error line + save button are toggled in place instead.
    const resolve = () => {
      const draft = getDraft(); if (!draft) return;
      const typed = inp.value.trim();
      setTyped(typed);
      const hit = this.listingByLabel(typed);
      draft.listing_id = hit ? String(hit.id) : null;
      draft.listing_name = hit ? String(hit.name) : '';
      const bad = !!typed && !hit;
      setBad(bad);
      const err = inp.parentElement?.querySelector<HTMLElement>('[data-listing-err]'); if (err) err.style.display = bad ? '' : 'none';
      const save = scope.querySelector<HTMLButtonElement>(attr === 'data-dlisting' ? '#tk-d-save' : '#tk-c-save'); if (save) save.disabled = bad;
    };
    inp.addEventListener('input', () => { setTyped(inp.value); if (this.listingByLabel(inp.value)) resolve(); });
    inp.addEventListener('change', resolve);
  }
  // R104 #8: mirrors lib/taskChecklist.checklistProgress — parent = 1 unit, each sub-item = 1 unit.
  private ckProgress(list: any[]): { done: number; total: number } {
    let done = 0, total = 0;
    for (const it of (Array.isArray(list) ? list : [])) {
      total++; if (it && it.done) done++;
      for (const sb of (Array.isArray(it?.sub) ? it.sub : [])) { total++; if (sb && sb.done) done++; }
    }
    return { done, total };
  }
  private personName(email: string | null): string {
    if (!email) return '';
    const p = this.people.find(x => String(x.email || '').toLowerCase() === String(email).toLowerCase());
    return p ? (p.full_name || p.email) : email;
  }
  private personRole(email: string): string {
    const p = this.people.find(x => String(x.email || '').toLowerCase() === String(email).toLowerCase());
    return p ? String(p.role || '').replace(/_/g, ' ') : '';
  }
  private statusLabel(s: string): string {
    const m = STATUSES.find(([k]) => k === s);
    return m ? m[1] : (s || '');
  }
  private taskById(id: string | null): any | null {
    return id ? ((this.tasks || []).find(t => t.id === id) || null) : null;
  }
  private cloneTask(t: any): any { return JSON.parse(JSON.stringify(t)); }
  // Draft and base are seeded the same way, so a legacy row's missing
  // checklist never reads as a change.
  private seedDraft(t: any): any { const d = this.cloneTask(t); if (!Array.isArray(d.checklist)) d.checklist = []; return d; }
  // R115 fix (review tasks 0): a newer server copy of the OPEN task (a save /
  // status / comment echo, or the task a 409 hands back) — fold it into the
  // draft: keys the user has not touched take the server's value, theirs stay
  // (people + checklist merge both sides). `against` = the copy the local
  // edits are relative to (the base, or — when the panel's save queue drains
  // — the capture of its newest save that landed: rebasePanel).
  private foldIntoDraft(fresh: any, against: any = this.draftBase): void {
    if (!this.draft || !fresh || this.draft.id !== fresh.id) return;
    if (this.olderThanPanel(fresh)) return;   // R115 fix 3: a late, older answer — the panel already holds a newer copy
    this.draft = this.seedDraft(mergeDraft(this.draft, against, fresh));
    this.draftBase = this.seedDraft(fresh);
  }
  // R115 fix 3: any server copy of the open task that is not the panel's own
  // re-base (a 409's copy, an echo, a status / comment / photo answer). While
  // a save from this panel opening is pending it is only adopted (serverCopy
  // + the list row — comments / activity render from the row): re-basing the
  // panel then would put the captures of one opening on two layers (a value
  // another user set would read as the user's own edit), and a copy that
  // already carries a pending save would read an undo queued behind it
  // (tick → untick) as "untouched". The panel takes it when its queue drains
  // (rebasePanel); with nothing pending it folds at once.
  private foldAnswer(fresh: any): void {
    if (!this.draft || !fresh || this.draft.id !== fresh.id) return;
    if (this.savePending(fresh.id)) return;
    this.foldIntoDraft(fresh);
  }
  private savePending(id: string): boolean {
    const j = this.lastJob.get(id);
    return !!j && !j.done && j.epoch === this.drawerEpoch;
  }
  // R115 fix 3: `fresh` predates the copy the open panel is layered on (an
  // answer that arrived after a newer one) — folding it would move the
  // panel's base backwards.
  private olderThanPanel(fresh: any): boolean {
    return !!this.draftBase && String(fresh.updated_at || '') < String(this.draftBase.updated_at || '');
  }
  // R115 fix 2 (verifier D0): every server answer that carries a task lands
  // here — keyed by the ANSWER's id (never "whatever panel is open now"). It
  // refreshes the list row and the per-task copy queued saves rebase on; an
  // older answer that arrives late (a reload already brought a newer copy)
  // changes nothing.
  private adoptServerCopy(fresh: any): void {
    if (!fresh || !fresh.id) return;
    const at = String(fresh.updated_at || '');
    const u = this.unsure.get(fresh.id);
    if (u && at > u.at) this.unsure.delete(fresh.id);   // a newer copy shows whether that save ran
    const prev = this.serverCopy.get(fresh.id);
    if (!prev || at >= String(prev.updated_at || '')) this.serverCopy.set(fresh.id, this.seedDraft(fresh));
    const t = this.taskById(fresh.id);
    if (t && at >= String(t.updated_at || '')) Object.assign(t, fresh);
  }
  private isOverdue(t: any): boolean {
    return !!t.due_date && t.due_date < this.localToday() && OPEN_STATUSES.includes(t.status);
  }
  private commentCount(t: any): number { return Array.isArray(t.comments) ? t.comments.length : 0; }

  // ── R112 visual atoms: avatar discs, due pill, progress ring ──
  // Initials from a display name ("Donielle Ortega" → DO, "debbie-inspections"
  // → DI); an email that isn't on the roster falls back to its local part.
  private initials(email: string): string {
    const raw = this.personName(email) || String(email || '');
    const base = raw.includes('@') ? raw.split('@')[0] : raw;
    const parts = base.split(/[\s._\-]+/).filter(Boolean);
    const s = parts.length >= 2 ? parts[0][0] + parts[1][0] : base.slice(0, 2);
    return s.toUpperCase();
  }
  private avTone(email: string): number {
    let h = 0; const s = String(email || '').toLowerCase();
    for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
    return h % AV_TONES;
  }
  private avatar(email: string, cls = ''): string {
    const name = this.personName(email) || email;
    return `<span class="tk-av t${this.avTone(email)} ${cls}" title="${this.esc(name)}">${this.esc(this.initials(email))}</span>`;
  }
  // Up to 3 overlapping discs + "+N"; unassigned reads as a quiet dashed disc.
  private avatarStack(emails: string[], max = 3): string {
    if (!emails.length) return `<span class="tk-avs" title="Unassigned"><span class="tk-av none">—</span></span>`;
    const shown = emails.slice(0, max);
    const rest = emails.length - shown.length;
    const names = emails.map(e => this.personName(e) || e).join(', ');
    return `<span class="tk-avs" title="${this.esc(names)}">${shown.map(e => this.avatar(e)).join('')}${rest > 0 ? `<span class="tk-av more">+${rest}</span>` : ''}</span>`;
  }
  // Due pill: overdue red, today amber, tomorrow/future quiet, none faint. Closed tasks never read as overdue.
  private duePill(t: any): string {
    if (!t.due_date) return `<span class="pill tk-pill-quiet faint">No date</span>`;
    const today = this.localToday();
    const open = OPEN_STATUSES.includes(t.status);
    const label = this.fmtDate(t.due_date);
    // R116 #7: a timed task (an MTR tour's showing, …) shows its time in the pill
    const tm = /^\d{2}:\d{2}$/.test(String(t.due_time || '')) ? this.fmtTime(t.due_time) : '';
    const at = tm ? ` · ${this.esc(tm)}` : '';
    const atT = tm ? ` at ${this.esc(tm)}` : '';
    if (open && t.due_date < today) return `<span class="pill pill-red" title="Due ${this.esc(label)}${atT} — overdue">${icon('clock', 11)} ${this.esc(label)}${at}</span>`;
    if (open && t.due_date === today) return `<span class="pill pill-amber" title="Due today${atT}">Today${at}</span>`;
    if (open && t.due_date === this.daysAgoStr(-1)) return `<span class="pill tk-pill-quiet" title="Due tomorrow${atT}">Tomorrow${at}</span>`;
    return `<span class="pill tk-pill-quiet" title="Due ${this.esc(label)}${atT}">${this.esc(label)}${at}</span>`;
  }
  // 'HH:mm' → '1:00 PM' (wall time — no zone involved)
  private fmtTime(hm: string): string {
    const m = /^(\d{2}):(\d{2})/.exec(String(hm || ''));
    if (!m) return '';
    const h = Number(m[1]);
    return `${((h + 11) % 12) + 1}:${m[2]} ${h < 12 ? 'AM' : 'PM'}`;
  }
  // R116 #7: an MTR tour's task — the tour it belongs to + "dates differ" when
  // the task's due day was moved off the tour's day (the tour never moves by itself).
  private tourOf(t: any): { lead_id: string; tour_id: string; tour_at: string; status: string; differ: boolean } | null {
    const sr = t && t.source_ref;
    if (!sr || sr.kind !== 'mtr-tour' || !sr.lead_id || !sr.tour_id) return null;
    const tourAt = String(sr.tour_at || '');
    const status = String(sr.tour_status || 'scheduled');
    const differ = OPEN_STATUSES.includes(t.status) && status === 'scheduled' && !!t.due_date && !!tourAt && t.due_date !== tourAt.slice(0, 10);
    return { lead_id: String(sr.lead_id), tour_id: String(sr.tour_id), tour_at: tourAt, status, differ };
  }
  private tourDifferPill(t: any): string {
    const tr = this.tourOf(t);
    return tr && tr.differ ? `<span class="pill pill-amber" title="The task is due ${this.esc(this.fmtDate(t.due_date))} but the tour is still ${this.esc(this.fmtDate(tr.tour_at.slice(0, 10)))} — open the task to line them up">⚠ dates differ</span>` : '';
  }
  // MTR leads hold renter details — only the MTR office roles may open one.
  private canOpenMtr(): boolean { return ['admin', 'director', 'manager', 'remote_ops'].includes(TasksPage.fieldRole()); }
  // Checklist progress ring (pathLength=100 → the dash is the percentage).
  private ringHtml(done: number, total: number): string {
    if (!total) return '';
    const pct = Math.round((done / total) * 100);
    return `<span class="tk-meta-i" title="Checklist ${done}/${total}"><svg class="tk-ring ${done >= total ? 'full' : ''}" viewBox="0 0 20 20" aria-hidden="true"><circle class="bg" cx="10" cy="10" r="8" pathLength="100"></circle><circle class="fg" cx="10" cy="10" r="8" pathLength="100" stroke-dasharray="${pct} 100"></circle></svg>${done}/${total}</span>`;
  }
  private metaHtml(t: any): string {
    const ckp = this.ckProgress(t.checklist);
    const n = this.commentCount(t);
    return `<span class="tk-meta">${this.ringHtml(ckp.done, ckp.total)}${n ? `<span class="tk-meta-i" title="${n} comment${n === 1 ? '' : 's'}">${icon('chat', 12)} ${n}</span>` : ''}</span>`;
  }
  private propertyChip(t: any): string {
    return t.listing_name ? `<span class="tk-chip tk-prop">${icon('home', 10)} ${this.esc(t.listing_name)}</span>` : '';
  }
  // Where the task came from: link {kind: wo|board|reservation, id} or source_ref {kind:'issues-board'}.
  private sourceLink(t: any): { kind: string; id: string; label: string } | null {
    if (!t) return null;
    const l = t.link && t.link.kind && t.link.id ? t.link : null;
    if (l && l.kind === 'wo') return { kind: 'wo', id: String(l.id), label: 'Work order' };
    if (l && l.kind === 'reservation') return { kind: 'reservation', id: String(l.id), label: 'Reservation' };
    if (l && l.kind === 'mtr_lead') return { kind: 'mtr_lead', id: String(l.id), label: 'MTR lead' };   // R116 #7
    const boardId = (l && l.kind === 'board') ? String(l.id) : (t.source_ref && t.source_ref.kind === 'issues-board' && Array.isArray(t.source_ref.board_ids) && t.source_ref.board_ids.length ? String(t.source_ref.board_ids[0]) : '');
    if (boardId) return { kind: 'board', id: boardId, label: 'Issues board' };
    return null;
  }
  private goSource(kind: string, id: string): void {
    try {
      if (kind === 'wo') { sessionStorage.setItem('mn_focus_wo', id); document.dispatchEvent(new CustomEvent('app:navigate', { detail: 'maintenance' })); return; }
      if (kind === 'board') { sessionStorage.setItem('iss_focus', id); document.dispatchEvent(new CustomEvent('app:navigate', { detail: 'issues' })); return; }
      // R116 #7: the MTR page's one-shot keys open that lead's Pipeline card
      if (kind === 'mtr_lead') { sessionStorage.setItem('mtr_tab', 'leads'); sessionStorage.setItem('mtr_open_lead', id); document.dispatchEvent(new CustomEvent('app:navigate', { detail: 'mtr' })); return; }
    } catch { /* private mode — still navigate */ }
    if (kind === 'reservation') document.dispatchEvent(new CustomEvent('app:navigate', { detail: 'reservations' }));
  }
  // Segmented control (status / priority) — a second, synced control over the
  // Details-card selects; attr is data-dstatus / data-dpriority / data-cstatus / data-cpriority.
  private segHtml(attr: string, opts: Array<[string, string]>, value: string): string {
    return `<div class="seg-tabs tk-seg" role="tablist">${opts.map(([k, l]) => `<button type="button" class="seg-tab ${value === k ? 'active' : ''}" ${attr}="${k}">${l}</button>`).join('')}</div>`;
  }

  // ── cleaner turnover context (My Work, read-only) ─────────────
  // Resolve the logged-in cleaner to their ao_cleaners roster id, then pull
  // their turns and keep today's. R120 (Madel: "an invited cleaner can't see
  // her assigned cleans"): through lib/rosterIdentity (contract C8) — the
  // admin's tech-link wins only while its row is still on the ACTIVE roster;
  // a link to a removed row falls back to the active row with the login's
  // email (the old code trusted the link blindly and showed the removed row's
  // empty day); a roster that didn't load keeps the link.
  private async loadCleanerTurns(): Promise<void> {
    try {
      const [me, j] = await Promise.all([
        fetch('/api/me').then(r => r.ok ? r.json() : null).catch(() => null),
        fetch('/api/assetops/cleaners').then(r => r.ok ? r.json() : null).catch(() => null),
      ]);
      const rows = j && Array.isArray(j.cleaners) ? j.cleaners : null;
      const email = TasksPage.profileEmail() || String((me && me.profile && me.profile.email) || '');
      const res = resolveRosterId({ link: (me && me.tech_link) || null, kind: 'cleaner', rows: rows || [], email, rowsLoaded: !!rows });
      const rosterId = res.id;
      if (!rosterId) { this.myTurns = []; return; }
      const d = await fetch(`/api/assetops/cleaners/${encodeURIComponent(rosterId)}`).then(r => r.ok ? r.json() : null).catch(() => null);
      const today = this.localToday();
      this.myTurns = ((d && d.turns) || []).filter((t: any) => t.check_out === today);
    } catch (_) { this.myTurns = []; }
  }

  // ── data shaping ──────────────────────────────────────────────
  // ignoreStatus: the board's columns ARE the status, so it filters on everything else.
  private filtered(ignoreStatus = false): any[] {
    const q = this.fSearch.trim().toLowerCase();
    return (this.tasks || []).filter(t =>
      (ignoreStatus || this.fStatus === 'all' || (this.fStatus === 'open' ? OPEN_STATUSES.includes(t.status) : t.status === this.fStatus)) &&
      (this.fAssignee === 'all' || (this.fAssignee === 'none' ? this.taskAssignees(t).length === 0 : this.taskAssignees(t).some(a => String(a).toLowerCase() === this.fAssignee.toLowerCase()))) &&
      (this.fPriority === 'all' || (t.priority || 'normal') === this.fPriority) &&
      (this.fListing === 'all' || String(t.listing_id || '') === this.fListing) &&
      (!q || String(t.title || '').toLowerCase().includes(q)));
  }
  // R120 (D33): due-first by default — lib/taskSort.mjs; 'Priority' in the
  // Sort select is the exact pre-R120 order.
  private sortOpen(rows: any[]): any[] {
    return sortTasks(rows, this.sortMode);
  }

  // ── draw ──────────────────────────────────────────────────────
  private draw(): void {
    const body = document.getElementById('tk-body');
    if (!body) return;
    if (!this.tasks) {
      body.innerHTML = emptyState('Tasks unavailable', 'GET /api/tasks failed — check the server log.', '⚠️');
      return;
    }
    // R111: the view switch is the shared .seg-tabs; bind() reads data-view,
    // only the active-class spelling changed ('on' → 'active').
    const pills = this.isField() ? '' : `
      <div class="seg-tabs tk-views" role="tablist">
        <button class="seg-tab ${this.view === 'my' ? 'active' : ''}" data-view="my">My work</button>
        <button class="seg-tab ${this.view === 'list' ? 'active' : ''}" data-view="list">List</button>
      </div>`;
    // R112: list rows vs kanban board (only meaningful on the office List view).
    const layouts = (this.isField() || this.view !== 'list') ? '' : `
      <div class="seg-tabs tk-layouts" role="tablist" title="How the tasks are laid out">
        <button class="seg-tab ${this.layout === 'list' ? 'active' : ''}" data-layout="list">☰ Rows</button>
        <button class="seg-tab ${this.layout === 'board' ? 'active' : ''}" data-layout="board">▦ Board</button>
      </div>`;
    body.innerHTML = `
      <style>
        /* ── Page head (hidden inside a hub) + toolbar ── */
        .tk-intro { margin:0; }
        .tk-intro .dash-head { margin-bottom:16px; }
        .tk-bar { display:flex; align-items:center; gap:12px; margin-bottom:14px; flex-wrap:wrap; }
        .tk-bar .tk-spacer { flex:1; }
        .tk-note { font-size:12px; color:var(--success-text); background:var(--success-soft); border:1px solid var(--success-border); border-radius:var(--radius-sm); padding:8px 12px; margin-bottom:12px; }
        .tk-dc-banner { border:1px solid var(--warning); background:var(--warning-soft); color:var(--warning-text); border-radius:var(--radius-sm); padding:9px 14px; margin-bottom:12px; font-size:12px; line-height:1.5; }
        .tk-dc-banner [data-tk-dc-toggle] { cursor:pointer; font-weight:600; }
        .tk-dc-list { margin-top:7px; display:flex; flex-direction:column; gap:3px; color:var(--text); }
        .tk-dc-list .tk-dc-note { color:var(--text-muted); }
        /* ── KPI band: main.css .kpi-card at the Calendar's density ── */
        .kpi-grid.tk-kpis { grid-template-columns:repeat(5, 1fr); gap:12px; margin-bottom:16px; }
        .tk-kpis .kpi-card { padding:14px 16px; }
        .tk-kpis .kpi-card-value { font-size:24px; margin-top:10px; }
        .tk-kpis .kpi-card-ic { width:34px; height:34px; border-radius:10px; }
        .tk-kpis .kpi-card-cap { margin-top:4px; }
        /* ── Filter bar: .input selects + an icon search ── */
        .tk-filters { display:flex; gap:8px; align-items:center; flex-wrap:wrap; margin-bottom:12px; }
        .tk-filters .input { width:auto; min-width:140px; padding:8px 10px; }
        .tk-search { position:relative; flex:1; min-width:200px; display:flex; align-items:center; color:var(--text-faint); }
        .tk-search svg { position:absolute; left:11px; pointer-events:none; }
        .tk-search .input { width:100%; min-width:0; padding-left:32px; }
        /* ── Atoms: avatar discs, quiet pills, progress ring, meta ── */
        .tk-av { width:24px; height:24px; border-radius:50%; display:inline-flex; align-items:center; justify-content:center; font-size:9.5px; font-weight:700; letter-spacing:0.02em; border:2px solid var(--card); flex:none; box-sizing:border-box; }
        .tk-av.t0 { background:var(--accent-soft); color:var(--accent-hover); }
        .tk-av.t1 { background:var(--success-soft); color:var(--success-text); }
        .tk-av.t2 { background:var(--warning-soft); color:var(--warning-text); }
        .tk-av.t3 { background:var(--danger-soft); color:var(--danger-text); }
        .tk-av.t4 { background:var(--bg-subtle); color:var(--text-muted); }
        .tk-av.t5 { background:var(--info-soft); color:var(--info-text); }
        .tk-av.more { background:var(--bg-subtle); color:var(--text-muted); font-size:9px; }
        .tk-av.none { background:transparent; border:1.5px dashed var(--border-strong); color:var(--text-faint); }
        .tk-av.lg { width:28px; height:28px; font-size:10.5px; }
        .tk-avs { display:inline-flex; align-items:center; }
        .tk-avs .tk-av + .tk-av { margin-left:-7px; }
        .pill.tk-pill-quiet { background:var(--bg-subtle); color:var(--text-muted); border:1px solid var(--border); }
        .pill.tk-pill-quiet.faint { color:var(--text-faint); border-style:dashed; }
        .pill svg { flex:none; }
        .tk-ring { width:18px; height:18px; transform:rotate(-90deg); flex:none; }
        .tk-ring circle { fill:none; stroke-width:3; }
        .tk-ring .bg { stroke:var(--border); }
        .tk-ring .fg { stroke:var(--accent); stroke-linecap:round; }
        .tk-ring.full .fg { stroke:var(--success); }
        .tk-meta { display:inline-flex; align-items:center; gap:10px; font-size:11.5px; color:var(--text-muted); font-variant-numeric:tabular-nums; }
        .tk-meta-i { display:inline-flex; align-items:center; gap:4px; white-space:nowrap; }
        .tk-chip { display:inline-flex; align-items:center; gap:4px; font-size:10.5px; font-weight:600; padding:2px 9px; border-radius:var(--radius-pill); background:var(--bg-subtle); border:1px solid var(--border); color:var(--text-muted); line-height:1.5; max-width:100%; }
        .tk-chip.tk-prop { color:var(--text); }
        .tk-chip svg { flex:none; color:var(--text-faint); }
        /* ── List rows: one rounded white card, hover bg-subtle ── */
        .tk-list { background:var(--card); border:1px solid var(--border); border-radius:var(--radius); overflow:hidden; box-shadow:var(--shadow-card); }
        .tk-list-h { display:grid; grid-template-columns:minmax(0,1fr) 112px 118px 96px 150px; gap:12px; padding:9px 18px; border-bottom:1px solid var(--border); background:var(--bg-subtle); font-size:10.5px; font-weight:600; text-transform:uppercase; letter-spacing:0.06em; color:var(--text-muted); }
        .tk-row { display:grid; grid-template-columns:minmax(0,1fr) 112px 118px 96px 150px; gap:12px; align-items:center; padding:12px 18px; border-bottom:1px solid var(--border-soft); cursor:pointer; transition:background .12s ease; }
        .tk-row:last-child { border-bottom:none; }
        .tk-row:hover { background:var(--bg-subtle); }
        .tk-row.closed .tk-row-title { color:var(--text-faint); text-decoration:line-through; }
        .tk-row-main { min-width:0; }
        .tk-row-title { font-weight:600; font-size:13.5px; color:var(--text); overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
        .tk-row-sub { margin-top:5px; display:flex; gap:5px; flex-wrap:wrap; align-items:center; }
        .tk-status.input { padding:6px 8px; font-size:11.5px; }
        /* ── Board: four status columns, cards drag between them ── */
        .tk-board { display:flex; gap:14px; align-items:flex-start; overflow-x:auto; padding-bottom:8px; scroll-snap-type:x proximity; }
        .tk-col { flex:1 1 0; min-width:230px; background:var(--bg-subtle); border:1px solid var(--border); border-radius:var(--radius); display:flex; flex-direction:column; max-height:calc(100vh - 140px); scroll-snap-align:start; }
        .tk-col-h { display:flex; align-items:center; gap:8px; padding:12px 14px 8px; font-size:12px; font-weight:700; color:var(--text); }
        .tk-col-dot { width:8px; height:8px; border-radius:50%; background:var(--text-faint); flex:none; }
        .tk-col[data-colkey="todo"] .tk-col-dot { background:var(--accent-fill); }
        .tk-col[data-colkey="in_progress"] .tk-col-dot { background:var(--accent); }
        .tk-col[data-colkey="blocked"] .tk-col-dot { background:var(--warning); }
        .tk-col[data-colkey="done"] .tk-col-dot { background:var(--success); }
        .tk-col-n { margin-left:auto; font-size:10.5px; font-weight:700; color:var(--text-muted); background:var(--card); border:1px solid var(--border); border-radius:var(--radius-pill); padding:1px 8px; font-variant-numeric:tabular-nums; }
        .tk-col-sub { font-size:10.5px; font-weight:500; color:var(--text-faint); }
        .tk-col-body { flex:1; overflow-y:auto; padding:4px 10px 10px; display:flex; flex-direction:column; gap:8px; min-height:96px; border-radius:0 0 var(--radius) var(--radius); transition:background .12s ease, box-shadow .12s ease; }
        .tk-col-body.over { background:var(--accent-soft); box-shadow:inset 0 0 0 2px var(--accent-border); }
        .tk-bcard { background:var(--card); border:1px solid var(--border); border-radius:var(--radius-btn); padding:11px 12px; box-shadow:var(--shadow-card); cursor:grab; transition:box-shadow .15s ease, border-color .15s ease, opacity .12s ease; }
        .tk-bcard:hover { box-shadow:var(--shadow-hover); border-color:var(--border-strong); }
        .tk-bcard:active { cursor:grabbing; }
        .tk-bcard.dragging { opacity:.45; }
        .tk-bcard.overdue { box-shadow:inset 3px 0 0 var(--danger), var(--shadow-card); }
        .tk-bcard.closed .tk-bcard-t { color:var(--text-muted); font-weight:500; }
        .tk-bcard.canceled .tk-bcard-t { color:var(--text-faint); text-decoration:line-through; }
        .tk-bcard-t { font-size:13px; font-weight:600; line-height:1.35; color:var(--text); overflow-wrap:anywhere; }
        .tk-bcard-sub { margin-top:6px; display:flex; gap:5px; flex-wrap:wrap; align-items:center; }
        .tk-bcard-f { margin-top:9px; display:flex; align-items:center; gap:8px; }
        .tk-bcard-f .tk-avs { margin-left:auto; }
        .tk-col-empty { font-size:11.5px; color:var(--text-faint); text-align:center; padding:18px 8px; border:1px dashed var(--border-strong); border-radius:var(--radius-sm); }
        .tk-col-more { font-size:11px; color:var(--text-faint); text-align:center; padding:6px 0 2px; }
        /* ── My Work cards: rounded, 40px done circle ── */
        .tk-sec-h { font-size:10.5px; font-weight:700; text-transform:uppercase; letter-spacing:0.09em; color:var(--text-faint); margin:18px 2px 10px; }
        .tk-sec-h:first-child { margin-top:0; }
        .tk-card { display:flex; gap:14px; align-items:center; border:1px solid var(--border); border-radius:var(--radius); background:var(--card); box-shadow:var(--shadow-card); padding:14px 16px; margin-bottom:10px; cursor:pointer; transition:box-shadow .15s ease, border-color .15s ease, background .12s ease; }
        .tk-card:hover { background:var(--bg-subtle); border-color:var(--border-strong); }
        .tk-card.overdue { border-color:var(--danger-border); }
        .tk-card-main { flex:1; min-width:0; }
        .tk-card-title { font-weight:600; font-size:14.5px; color:var(--text); }
        .tk-card-sub { font-size:12px; color:var(--text-muted); margin-top:6px; display:flex; gap:6px; flex-wrap:wrap; align-items:center; }
        .tk-done-circle { width:40px; height:40px; min-width:40px; border-radius:50%; border:2px solid var(--border); background:var(--card); color:transparent; font-size:16px; font-weight:700; cursor:pointer; display:inline-flex; align-items:center; justify-content:center; transition:background .15s ease, border-color .15s ease, color .15s ease; }
        .tk-done-circle:hover { border-color:var(--success); color:var(--success); background:var(--success-soft); }
        .tk-done-circle.on { background:var(--success); border-color:var(--success); color:var(--card); }
        .tk-cam { font-size:14px; padding:7px 10px; }
        /* Cleaner turnover cards — read-only context in the accent wash */
        .tk-turn { display:block; width:100%; text-align:left; border:1px dashed var(--accent-border); border-radius:var(--radius); background:var(--accent-soft); padding:13px 16px; margin-bottom:10px; cursor:pointer; font-family:inherit; }
        .tk-turn:hover { border-style:solid; }
        .tk-turn-title { font-weight:600; font-size:14px; color:var(--text); }
        .tk-turn-sub { font-size:11.5px; color:var(--text-muted); margin-top:3px; }
        .tk-turn-hint { font-style:italic; color:var(--accent); }
        .tk-empty { font-size:12.5px; color:var(--text-muted); padding:22px; text-align:center; }
        .tk-empty-card { background:var(--card); border:1px dashed var(--border); border-radius:var(--radius); }
        /* ── Task panel (right overlay) + create modal ── */
        .tk-overlay { position:fixed; inset:0; background:rgba(15,23,42,.45); z-index:9500; display:flex; justify-content:flex-end; }
        .tk-overlay.center { align-items:flex-start; justify-content:center; padding:40px 16px; overflow-y:auto; }
        .tk-drawer { width:100%; max-width:960px; height:100%; background:var(--card); box-shadow:var(--shadow-pop); display:flex; flex-direction:column; border-radius:var(--radius) 0 0 var(--radius); overflow:hidden; }
        .tk-panel-head { padding:16px 24px 14px; border-bottom:1px solid var(--border); background:var(--card); }
        .tk-panel-eyebrow { display:flex; align-items:center; gap:10px; font-size:11.5px; color:var(--text-muted); flex-wrap:nowrap; min-width:0; }
        .tk-panel-eyebrow .tk-eyebrow, .tk-panel-eyebrow .pill { flex:none; }
        .tk-panel-eyebrow .tk-meta-txt { flex:1; min-width:0; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
        .tk-panel-eyebrow .tk-eyebrow { font-size:10px; font-weight:700; letter-spacing:0.09em; text-transform:uppercase; color:var(--accent); background:var(--accent-soft); border-radius:var(--radius-pill); padding:2px 8px; }
        .tk-panel-eyebrow .tk-x { margin-left:auto; }
        .tk-x { border:none; background:none; font-size:16px; color:var(--text-muted); cursor:pointer; padding:2px 7px; border-radius:var(--radius-sm); line-height:1.3; }
        .tk-x:hover { background:var(--border-soft); color:var(--text); }
        .tk-title-in { display:block; width:100%; box-sizing:border-box; margin:8px 0 0 -8px; padding:5px 8px; border:1px solid transparent; border-radius:var(--radius-sm); background:transparent; font:inherit; font-size:20px; font-weight:700; letter-spacing:-0.01em; color:var(--text); }
        .tk-title-in:hover { background:var(--bg-subtle); }
        .tk-title-in:focus { outline:none; background:var(--card); border-color:var(--accent); box-shadow:var(--focus-ring); }
        .tk-title-in::placeholder { color:var(--text-faint); font-weight:600; }
        .tk-panel-segs { display:flex; gap:10px; align-items:center; flex-wrap:wrap; margin-top:12px; }
        .tk-seg-lab { font-size:10.5px; font-weight:600; text-transform:uppercase; letter-spacing:0.06em; color:var(--text-faint); margin-right:-2px; }
        .seg-tabs.tk-seg { padding:2px; border-radius:var(--radius-btn); }
        .tk-seg .seg-tab { padding:5px 11px; font-size:11.5px; border-radius:var(--radius-sm); }
        .tk-seg .seg-tab.active[data-dstatus="done"], .tk-seg .seg-tab.active[data-cstatus="done"] { background:var(--success-soft); color:var(--success-text); }
        .tk-seg .seg-tab.active[data-dstatus="blocked"], .tk-seg .seg-tab.active[data-cstatus="blocked"] { background:var(--warning-soft); color:var(--warning-text); }
        .tk-seg .seg-tab.active[data-dstatus="canceled"], .tk-seg .seg-tab.active[data-cstatus="canceled"] { background:var(--bg-subtle); color:var(--text-muted); }
        .tk-seg .seg-tab.active[data-dpriority="urgent"], .tk-seg .seg-tab.active[data-cpriority="urgent"] { background:var(--danger-soft); color:var(--danger-text); }
        .tk-seg .seg-tab.active[data-dpriority="high"], .tk-seg .seg-tab.active[data-cpriority="high"] { background:var(--warning-soft); color:var(--warning-text); }
        .tk-seg .seg-tab.active[data-dpriority="low"], .tk-seg .seg-tab.active[data-cpriority="low"] { background:var(--bg-subtle); color:var(--text-muted); }
        .tk-drawer-body { flex:1; overflow-y:auto; padding:20px 24px 28px; background:var(--bg); }
        .tk-panel-grid { display:grid; grid-template-columns:minmax(0,1fr) 320px; gap:20px; align-items:start; }
        .tk-pane-main { min-width:0; display:flex; flex-direction:column; gap:14px; }
        .tk-pane-side { min-width:0; display:flex; flex-direction:column; gap:14px; }
        .tk-sec { background:var(--card); border:1px solid var(--border); border-radius:var(--radius); box-shadow:var(--shadow-card); padding:14px 16px; }
        .tk-sec-head { display:flex; align-items:center; gap:10px; margin-bottom:10px; }
        .tk-sec-head h4 { margin:0; font-size:10.5px; font-weight:700; text-transform:uppercase; letter-spacing:0.09em; color:var(--text-faint); }
        .tk-sec-head .tk-sec-n { font-size:11px; color:var(--text-muted); font-variant-numeric:tabular-nums; }
        .tk-sec-head .tk-prog { flex:1; max-width:160px; margin-left:auto; }
        .tk-prog { height:6px; border-radius:var(--radius-pill); background:var(--bg-subtle); border:1px solid var(--border-soft); overflow:hidden; }
        .tk-prog i { display:block; height:100%; background:var(--accent); border-radius:var(--radius-pill); transition:width .2s ease; }
        .tk-prog.full i { background:var(--success); }
        textarea.input.tk-desc { min-height:96px; resize:vertical; font-size:13px; line-height:1.5; }
        .tk-form .input, .tk-ck-detail .input, .tk-kv .input { font-size:13px; text-transform:none; letter-spacing:0; font-weight:400; }
        .tk-field-err { display:block; text-transform:none; letter-spacing:0; font-weight:400; font-size:11.5px; color:var(--danger-text); margin-top:4px; }
        /* Details card: label | control rows */
        .tk-kv-row { display:grid; grid-template-columns:84px minmax(0,1fr); gap:10px; align-items:center; padding:8px 0; border-bottom:1px solid var(--border-soft); font-size:12.5px; }
        .tk-kv-row:last-child { border-bottom:none; }
        /* R116 #7: due date + optional time; the MTR tour block */
        .tk-due-pair { display:flex; flex-wrap:wrap; gap:6px; min-width:0; }
        .tk-due-pair input[type="date"] { flex:1 1 148px; min-width:0; }
        .tk-due-pair input[type="time"] { flex:1 1 112px; min-width:0; }
        .tk-tour { display:flex; flex-direction:column; gap:8px; margin-bottom:14px; }
        .tk-tour .tk-sec-head { margin-bottom:0; }
        .tk-tour .tk-sec-head .pill { margin-left:auto; }
        .tk-tour-when { font-size:13.5px; font-weight:700; color:var(--text); font-variant-numeric:tabular-nums; }
        .tk-tour-home { font-weight:500; color:var(--text-muted); }
        .tk-tour-differ { font-size:12px; line-height:1.45; padding:9px 11px; border-radius:var(--radius-sm); background:var(--warning-soft); color:var(--warning-text); border:1px solid var(--warning-border); }
        .tk-tour-acts { display:flex; flex-wrap:wrap; gap:6px; margin-top:8px; }
        .tk-tour-acts .btn { min-height:34px; }
        .tk-tour-hint { font-size:11.5px; line-height:1.45; color:var(--text-muted); }
        .tk-tour .tk-kv-link { align-self:flex-start; }
        @media (max-width: 640px) { .tk-tour-acts .btn { min-height:36px; } .tk-due-pair input { font-size:16px; } }
        .tk-kv-row.top { align-items:flex-start; }
        .tk-kv-row.top .tk-kv-k { padding-top:9px; }
        .tk-kv-k { font-size:10.5px; font-weight:600; text-transform:uppercase; letter-spacing:0.06em; color:var(--text-muted); }
        .tk-kv-v { min-width:0; color:var(--text); overflow-wrap:anywhere; }
        .tk-kv-v .input { padding:7px 10px; }
        .tk-kv-v.muted { color:var(--text-muted); font-size:12px; }
        .tk-kv-link { display:inline-flex; align-items:center; gap:6px; padding:5px 10px; border-radius:var(--radius-btn); border:1px solid var(--accent-border); background:var(--accent-soft); color:var(--accent-hover); font:inherit; font-size:12px; font-weight:600; cursor:pointer; }
        .tk-kv-link:hover { background:var(--card); }
        /* Assignee picker: selected chips + search + menu */
        .tk-as-sel { display:flex; flex-wrap:wrap; gap:6px; margin-bottom:6px; }
        .tk-as-chip { display:inline-flex; align-items:center; gap:6px; padding:2px 6px 2px 2px; border-radius:var(--radius-pill); background:var(--card); border:1px solid var(--border); font-size:12px; font-weight:600; color:var(--text); max-width:100%; }
        .tk-as-chip .tk-av { border-color:transparent; }
        .tk-as-name { overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
        .tk-as-rm { border:none; background:none; color:var(--text-faint); cursor:pointer; font-size:11px; padding:0 3px; line-height:1; border-radius:50%; }
        .tk-as-rm:hover { color:var(--danger-text); }
        .tk-as-none { font-size:12px; color:var(--text-faint); padding:3px 0; }
        .tk-as-search { position:relative; display:flex; align-items:center; color:var(--text-faint); }
        .tk-as-search svg { position:absolute; left:10px; pointer-events:none; }
        .tk-as-search .input { padding-left:30px; }
        .tk-as-menu { margin-top:6px; max-height:224px; overflow-y:auto; border:1px solid var(--border); border-radius:var(--radius-sm); background:var(--card); box-shadow:var(--shadow-card); padding:4px; }
        .tk-as-menu .tk-assignee-chip { display:flex; align-items:center; gap:9px; width:100%; text-align:left; border:none; background:transparent; padding:6px 8px; border-radius:var(--radius-sm); font:inherit; font-size:12.5px; color:var(--text); cursor:pointer; }
        .tk-as-menu .tk-assignee-chip:hover { background:var(--bg-subtle); }
        .tk-as-menu .tk-assignee-chip.active { background:var(--accent-soft); color:var(--accent-hover); }
        .tk-as-mname { flex:1; min-width:0; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; font-weight:600; }
        .tk-as-role { font-size:10.5px; color:var(--text-faint); text-transform:capitalize; }
        .tk-as-check { font-size:12px; font-weight:700; color:var(--accent); }
        /* Checklist */
        .tk-ck-item { display:flex; gap:8px; align-items:center; padding:7px 0; border-bottom:1px solid var(--border-soft); font-size:13px; }
        .tk-ck-item.done .tk-ck-title { color:var(--text-faint); text-decoration:line-through; }
        .tk-ck-title { flex:1; min-width:0; }
        .tk-ck { font-size:10.5px; color:var(--text-muted); font-weight:600; font-variant-numeric:tabular-nums; }
        .tk-ck-box { width:22px; height:22px; min-width:22px; border-radius:7px; border:1.5px solid var(--border); background:var(--card); cursor:pointer; font-size:12px; color:transparent; display:inline-flex; align-items:center; justify-content:center; }
        .tk-ck-box:hover { border-color:var(--success); }
        .tk-ck-item.done .tk-ck-box { background:var(--success); border-color:var(--success); color:var(--card); }
        .tk-ck-x { border:none; background:none; color:var(--text-faint); cursor:pointer; font-size:12px; }
        .tk-ck-x:hover { color:var(--danger-text); }
        .tk-ck-addrow { display:flex; gap:8px; margin-top:10px; }
        .tk-ck-addrow .input { flex:1; width:auto; font-size:13px; }
        .tk-ck-exp { border:none; background:none; color:var(--text-faint); cursor:pointer; font-size:11.5px; padding:0 4px; white-space:nowrap; font-family:inherit; }
        .tk-ck-exp.has { color:var(--text-muted); font-weight:700; }
        .tk-ck-hint { font-size:11px; color:var(--text-faint); font-weight:400; margin-top:1px; }
        .tk-ck-sub { padding-left:30px; border-bottom:1px dashed var(--border-soft); }
        .tk-ck-detail { margin:4px 0 8px 30px; padding:10px 12px; border:1px solid var(--border-soft); border-radius:var(--radius-sm); background:var(--bg-subtle); }
        .tk-ck-detail label { display:flex; flex-direction:column; gap:4px; font-size:10.5px; font-weight:600; color:var(--text-muted); text-transform:uppercase; letter-spacing:0.06em; margin-bottom:8px; }
        .tk-ck-detail textarea.input { font-size:12.5px; min-height:48px; resize:vertical; }
        .tk-ck-sublist { margin-top:4px; }
        .tk-ck-none { font-size:11.5px; color:var(--text-faint); }
        .tk-muted-note { font-size:12px; color:var(--text-faint); }
        /* Comments thread + composer */
        .tk-comment { display:flex; gap:10px; align-items:flex-start; margin-bottom:10px; }
        .tk-comment-b { flex:1; min-width:0; background:var(--bg-subtle); border:1px solid var(--border-soft); border-radius:var(--radius-sm); padding:8px 11px; }
        .tk-comment-h { font-size:11px; color:var(--text-muted); margin-bottom:3px; display:flex; gap:6px; align-items:baseline; }
        .tk-comment-h b { color:var(--text); font-weight:600; }
        .tk-comment-t { font-size:13px; line-height:1.5; white-space:pre-wrap; overflow-wrap:anywhere; }
        .tk-comment img { max-width:120px; border-radius:var(--radius-sm); border:1px solid var(--border); margin:6px 4px 0 0; }
        .tk-c-compose { margin-top:6px; display:flex; gap:10px; align-items:flex-start; }
        .tk-c-compose .tk-av { margin-top:4px; }
        .tk-c-box { flex:1; min-width:0; }
        .tk-c-compose textarea.input { min-height:58px; resize:vertical; font-size:13px; }
        .tk-c-actions { display:flex; gap:8px; align-items:center; margin-top:6px; flex-wrap:wrap; }
        .tk-c-actions .tk-send { margin-left:auto; }
        .tk-c-picked { font-size:11.5px; font-weight:600; color:var(--success-text); display:inline-flex; align-items:center; gap:4px; }
        .tk-linkbtn { border:none; background:none; cursor:pointer; color:var(--text-muted); font-size:12px; padding:0 4px; }
        .tk-linkbtn:hover { color:var(--danger-text); }
        .tk-log { font-size:11.5px; color:var(--text-muted); line-height:1.7; margin:6px 0 0; padding-left:16px; }
        /* R115 C1: @mentions — inline chip in comments/log + the textarea picker */
        .tk-mention { color:var(--accent); font-weight:600; background:var(--accent-soft); border-radius:4px; padding:0 3px; }
        .tk-mwrap { position:relative; }
        .tk-mwrap > textarea.input { display:block; width:100%; box-sizing:border-box; }
        .tk-mmenu { position:absolute; left:0; right:0; top:100%; z-index:30; margin-top:4px; box-shadow:var(--shadow-pop); }
        .tk-mwrap.up .tk-mmenu { top:auto; bottom:100%; margin:0 0 4px; }
        .tk-mmenu .tk-assignee-chip { min-height:36px; }
        .tk-mmenu-h { font-size:10.5px; font-weight:700; text-transform:uppercase; letter-spacing:0.06em; color:var(--text-faint); padding:6px 8px 4px; }
        /* R115 C2: Activity timeline — every change, newest first */
        .tk-tl { list-style:none; margin:12px 0 0; padding:0; position:relative; }
        .tk-tl::before { content:''; position:absolute; left:11px; top:6px; bottom:6px; width:2px; background:var(--border-soft); border-radius:2px; }
        .tk-tl-item { position:relative; display:flex; gap:10px; align-items:flex-start; padding:6px 0; }
        .tk-tl-item .tk-av { position:relative; z-index:1; }
        .tk-av.tk-av-sys { background:var(--bg-subtle); color:var(--text-muted); font-size:12px; }
        .tk-tl-b { flex:1; min-width:0; }
        .tk-tl-h { display:flex; gap:6px; align-items:baseline; flex-wrap:wrap; font-size:11px; color:var(--text-faint); }
        .tk-tl-h b { font-size:12px; font-weight:600; color:var(--text); }
        .tk-tl-t { font-size:12.5px; color:var(--text-muted); line-height:1.45; margin-top:1px; overflow-wrap:anywhere; }
        .tk-tl-chg { color:var(--text); font-weight:600; }
        .tk-tl-muted { color:var(--text-faint); font-size:11.5px; }
        .tk-tl-more { display:block; margin:6px 0 0 34px; border:none; background:transparent; color:var(--accent); font:inherit; font-size:12px; font-weight:600; padding:0 8px; min-height:36px; border-radius:var(--radius-btn); cursor:pointer; }
        .tk-tl-more:hover { background:var(--accent-soft); }
        /* R120: Activity filter (Changes | Comments | All), Show change, the pre-history footer */
        .seg-tabs.tk-log-f { margin:10px 0 0; padding:2px; border-radius:var(--radius-btn); display:inline-flex; flex-wrap:wrap; }
        .tk-log-f .seg-tab { padding:5px 11px; font-size:11.5px; min-height:32px; border-radius:var(--radius-sm); }
        .tk-log-n { font-size:10.5px; color:var(--text-faint); font-variant-numeric:tabular-nums; margin-left:2px; }
        .tk-log-f .seg-tab.active .tk-log-n { color:inherit; }
        .tk-tl-items { color:var(--text); }
        details.tk-tl-diff { display:inline; }
        details.tk-tl-diff > summary { display:inline-flex; align-items:center; cursor:pointer; color:var(--accent); font-size:11.5px; font-weight:600; list-style:none; padding:0 4px; border-radius:var(--radius-sm); }
        details.tk-tl-diff > summary::-webkit-details-marker { display:none; }
        details.tk-tl-diff > summary:hover { background:var(--accent-soft); }
        details.tk-tl-diff[open] > summary { margin-bottom:4px; }
        .tk-tl-was, .tk-tl-now { display:block; font-size:12px; line-height:1.45; padding:6px 9px; border-radius:var(--radius-sm); margin-top:4px; white-space:pre-wrap; overflow-wrap:anywhere; color:var(--text); }
        .tk-tl-was { background:var(--danger-soft); }
        .tk-tl-now { background:var(--success-soft); }
        .tk-tl-lab { display:block; font-size:10px; font-weight:700; text-transform:uppercase; letter-spacing:0.06em; color:var(--text-muted); margin-bottom:2px; }
        .tk-tl-foot { margin:10px 0 0; padding-top:8px; border-top:1px dashed var(--border); font-size:11.5px; line-height:1.45; color:var(--text-faint); }
        /* R120: "Save changes before closing?" over the panel */
        .tk-drawer { position:relative; }
        .tk-ask { position:absolute; inset:0; z-index:5; background:rgba(15,23,42,.35); display:flex; align-items:center; justify-content:center; padding:16px; }
        .tk-ask-card { width:100%; max-width:380px; background:var(--card); border:1px solid var(--border); border-radius:var(--radius); box-shadow:var(--shadow-pop); padding:18px 18px 14px; }
        .tk-ask-h { font-size:15px; font-weight:700; color:var(--text); }
        .tk-ask-t { margin-top:6px; font-size:12.5px; line-height:1.5; color:var(--text-muted); }
        .tk-ask-acts { display:flex; gap:8px; justify-content:flex-end; flex-wrap:wrap; margin-top:14px; }
        .tk-ask-acts .btn { min-height:36px; }
        @media (max-width: 720px) {
          .tk-log-f .seg-tab { min-height:36px; }
          details.tk-tl-diff > summary { min-height:36px; }
          .tk-ask-acts .btn { flex:1 1 auto; justify-content:center; }
        }
        /* R115: the people pickers' ✕ (assignees + the new watchers editor) is a 36px tap target on phones without growing the chip */
        @media (max-width: 720px) { .tk-as-rm { min-width:36px; min-height:36px; margin:-4px -6px -4px -4px; } }
        details.tk-details > summary { cursor:pointer; font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:0.09em; color:var(--text-faint); }
        details.tk-details > summary:hover { color:var(--text-muted); }
        .tk-details-body { margin-top:10px; }
        .tk-drawer-foot { display:flex; gap:8px; justify-content:flex-end; align-items:center; padding:12px 24px; border-top:1px solid var(--border); background:var(--card); }
        .tk-drawer-foot .tk-danger { margin-right:auto; color:var(--danger); }
        .tk-drawer-foot .tk-danger:hover { background:var(--danger-soft); border-color:var(--danger-border); }
        .tk-modal { background:var(--card); border-radius:var(--radius); box-shadow:var(--shadow-pop); width:100%; max-width:880px; overflow:hidden; max-height:92vh; display:flex; flex-direction:column; }
        .tk-modal .tk-drawer-body { padding:18px 24px 22px; }
        .tk-modal .tk-panel-grid { grid-template-columns:minmax(0,1fr) 300px; align-items:stretch; }
        .tk-modal .tk-pane-main .tk-sec { display:flex; flex-direction:column; height:100%; box-sizing:border-box; }
        .tk-modal textarea.input.tk-desc { flex:1; min-height:180px; }
        @media (max-width: 1100px) {
          .kpi-grid.tk-kpis { grid-template-columns:repeat(3, 1fr); }
          .tk-list-h, .tk-row { grid-template-columns:minmax(0,1fr) 112px 118px 150px; }
          .tk-row-meta, .tk-list-h .tk-h-meta { display:none; }
        }
        @media (max-width: 720px) {
          .kpi-grid.tk-kpis { grid-template-columns:repeat(2, 1fr); gap:10px; }
          .tk-list-h { display:none; }
          .tk-row { grid-template-columns:minmax(0,1fr) 120px; padding:12px 14px; }
          .tk-row-avs, .tk-row-due, .tk-row-meta { display:none; }
          .tk-row-title { white-space:normal; }
          .tk-board { gap:10px; scroll-snap-type:x mandatory; margin:0 -16px; padding:0 16px 8px; }
          .tk-col { flex:0 0 min(82vw, 320px); max-height:calc(100vh - 180px); }
          .tk-drawer { max-width:100%; border-radius:0; }
          .tk-panel-head { padding:14px 16px 12px; }
          .tk-title-in { font-size:18px; }
          .tk-panel-segs { flex-wrap:nowrap; overflow-x:auto; -webkit-overflow-scrolling:touch; margin:12px -16px 0; padding:0 16px 4px; }
          .tk-panel-segs .seg-tabs { flex:none; }
          .tk-drawer-body { padding:14px 16px 24px; }
          .tk-panel-grid, .tk-modal .tk-panel-grid { grid-template-columns:1fr; }
          .tk-pane-side { order:-1; }
          .tk-drawer-foot { padding:10px 16px; }
          .tk-overlay.center { padding:0; }
          .tk-modal { max-height:100vh; height:100%; border-radius:0; }
          .tk-filters .input { min-width:0; flex:1 1 44%; }
          .tk-search { flex-basis:100%; }
          .tk-bar .seg-tabs { width:100%; }   /* tabs first, full width; the button drops to its own row */
        }
        /* ── R115 mobile (CSS only): the bar was three full-width rows — now the
           two segs share a row (My work view: seg + "+ New task" on ONE row);
           the drawer's Status / Priority segs wrap instead of scrolling
           Priority off-screen; the assignee ✕ is a real tap target. */
        @media (max-width: 720px) {
          .tk-bar { display:grid; grid-template-columns:repeat(2, minmax(0, 1fr)); gap:8px; align-items:center; }
          .tk-bar .seg-tabs { min-width:0; }
          .tk-bar .seg-tab { flex:1; justify-content:center; padding:7px 8px; }
          .tk-bar .tk-spacer { display:none; }
          .tk-bar #tk-new { justify-content:center; min-width:0; }
          .tk-bar .tk-layouts ~ #tk-new, .tk-bar .tk-spacer:first-child ~ #tk-new { grid-column:1 / -1; }
          .tk-panel-segs { flex-wrap:wrap; overflow:visible; margin:12px 0 0; padding:0; row-gap:8px; }
          .tk-as-rm { min-width:28px; min-height:28px; font-size:13px; }
          .tk-kpis .kpi-card { padding:12px 14px; }   /* the shared compact phone KPI (main.css G4) */
          .tk-kpis .kpi-card-value { font-size:22px; margin-top:6px; }
          .tk-kpis .kpi-card-ic { width:28px; height:28px; border-radius:var(--radius-sm); }
          .tk-kpis .kpi-card:last-child:nth-child(odd) { grid-column:1 / -1; }
        }
      </style>
      <div class="page-intro tk-intro">
        <div class="dash-head">
          <h1>Tasks</h1>
          <p>${this.view === 'my' ? 'Your work for today, sorted by due date. Tap the circle when a task is done, the camera to attach proof.' : 'Every task across the portfolio. Filter, assign and close them here; the numbers follow the filters.'}</p>
        </div>
      </div>
      <div class="tk-bar">
        ${pills}
        ${layouts}
        <span class="tk-spacer"></span>
        <button type="button" class="btn btn-primary" id="tk-new">+ New task</button>
      </div>
      ${this.note ? `<div class="tk-note">${this.esc(this.note)}</div>` : ''}
      ${this.view === 'my' ? this.myViewHtml() : this.listViewHtml()}
      ${this.drawerId && this.draft ? this.drawerHtml() : ''}
      ${this.showCreate && this.cDraft ? this.createHtml() : ''}
    `;
    this.bind();
  }

  // ── MY WORK view ──────────────────────────────────────────────
  private myViewHtml(): string {
    const me = TasksPage.profileEmail().toLowerCase();
    const mine = me ? (this.tasks || []).filter(t => this.taskAssignees(t).some(a => String(a).toLowerCase() === me)) : [];
    // R120: the shared comparator (due, priority, then time — timed first —
    // and a deterministic tie-break); closed: newest completion first.
    const open = mine.filter(t => OPEN_STATUSES.includes(t.status)).sort(compareTasks);
    const closed = mine.filter(t => !OPEN_STATUSES.includes(t.status)).sort(compareTasks).slice(0, 20);
    // R57: these cards LOOK tappable, and cleaners tap them expecting their
    // job to open (video: 15 seconds of tapping into a dead <div>). Make the
    // whole card navigate to Housekeeping — the cleaner lands in her job view.
    const turns = this.myTurns.map(t => `
      <button type="button" class="tk-turn" data-tk-gohk>
        <div class="tk-turn-title">🧹 Turnover — ${this.esc(t.listing_name)}</div>
        <div class="tk-turn-sub">${t.same_day ? 'same-day · ' : ''}checkout today · ${this.esc(String(t.status || 'unstarted').replace(/_/g, ' '))} · <span class="tk-turn-hint">open in Housekeeping ▸</span></div>
      </button>`).join('');
    return `
      ${turns ? `<div class="tk-sec-h">Today's turnovers</div>${turns}` : ''}
      <div class="tk-sec-h">My tasks (${open.length} open)</div>
      ${open.length ? open.map(t => this.cardHtml(t)).join('')
        : `<div class="tk-empty tk-empty-card">${me ? 'Nothing assigned to you — enjoy the quiet.' : 'No profile email found — sign in again to see your tasks.'}</div>`}
      ${closed.length ? `
        <details class="tk-details" style="margin-top:18px;">
          <summary>Done (${closed.length} recent)</summary>
          <div class="tk-details-body">${closed.map(t => this.cardHtml(t)).join('')}</div>
        </details>` : ''}`;
  }

  private cardHtml(t: any): string {
    const overdue = this.isOverdue(t);
    const closed = !OPEN_STATUSES.includes(t.status);
    const id = this.esc(t.id);
    const pri = String(t.priority || 'normal');
    return `
      <div class="tk-card ${overdue ? 'overdue' : ''}" data-id="${id}">
        <button type="button" class="tk-done-circle ${t.status === 'done' ? 'on' : ''}" data-done="${id}" title="${t.status === 'done' ? 'Done' : 'Mark done'}">✓</button>
        <div class="tk-card-main">
          <div class="tk-card-title">${this.esc(t.title)}</div>
          <div class="tk-card-sub">
            ${pri === 'urgent' || pri === 'high' ? this.priorityPill(pri) : ''}
            ${this.propertyChip(t)}
            ${this.duePill(t)}
            ${this.tourDifferPill(t)}
            ${this.metaHtml(t)}
            ${closed || t.status !== 'todo' ? `<span class="pill tk-pill-quiet">${this.esc(this.statusLabel(t.status))}</span>` : ''}
          </div>
        </div>
        ${closed ? '' : `
          <button type="button" class="btn btn-secondary btn-sm tk-cam" data-cam="${id}" title="Attach a proof photo">📷</button>
          <input type="file" accept="image/*" multiple data-photo="${id}" style="display:none;">`}
      </div>`;
  }

  // ── LIST view (rows or board) ─────────────────────────────────
  private listViewHtml(): string {
    const board = this.layout === 'board';
    const rows = this.sortOpen(this.filtered(board));
    // R112 review: the board ignores the status filter, so `rows` carries every closed
    // task ever synced while the columns only place open cards + the Done window. The
    // KPI band counts the cards actually on the board — never the whole history.
    const cols = board ? this.boardColumns(rows) : null;
    const kpiRows = cols ? cols.flatMap(c => c.pool || c.items) : rows;   // fix-verify [9]: the Done window UNCAPPED, so "Done this week" stays true past 40 cards
    const statusOpts = [
      `<option value="open" ${this.fStatus === 'open' ? 'selected' : ''}>Open (any)</option>`,
      `<option value="all" ${this.fStatus === 'all' ? 'selected' : ''}>Any status</option>`,
      ...STATUSES.map(([k, l]) => `<option value="${k}" ${this.fStatus === k ? 'selected' : ''}>${l}</option>`),
    ].join('');
    const assigneeOpts = [
      `<option value="all" ${this.fAssignee === 'all' ? 'selected' : ''}>All assignees</option>`,
      `<option value="none" ${this.fAssignee === 'none' ? 'selected' : ''}>Unassigned</option>`,
      ...this.people.map(p => `<option value="${this.esc(p.email)}" ${this.fAssignee === p.email ? 'selected' : ''}>${this.esc(p.full_name)}</option>`),
    ].join('');
    const priorityOpts = [
      `<option value="all" ${this.fPriority === 'all' ? 'selected' : ''}>All priorities</option>`,
      ...PRIORITIES.map(([k, l]) => `<option value="${k}" ${this.fPriority === k ? 'selected' : ''}>${l}</option>`),
    ].join('');
    const listingOpts = [
      `<option value="all" ${this.fListing === 'all' ? 'selected' : ''}>All properties</option>`,
      ...this.listings.map(l => `<option value="${this.esc(l.id)}" ${this.fListing === String(l.id) ? 'selected' : ''}>${this.esc(l.name)}</option>`),
    ].join('');
    const dcTouched = this.dateChanges.filter(c => (c.tasks_moved || 0) + (c.tasks_flagged || 0) > 0);
    const dcBanner = dcTouched.length ? `
      <div class="tk-dc-banner">
        <span data-tk-dc-toggle>📅 ${dcTouched.length} reservation date change${dcTouched.length === 1 ? '' : 's'} touched tasks — ${this.dcOpen ? 'hide' : 'show'}</span>
        ${this.dcOpen ? `<div class="tk-dc-list">
          ${dcTouched.slice(0, 12).map(c => `<div><strong>${this.esc(c.guest)}</strong> · ${this.esc(this.listings.find(l => String(l.id) === String(c.listing_id))?.name || c.listing_id)} — ${this.esc(c.old_ci)}→${this.esc(c.new_ci)} / ${this.esc(c.old_co)}→${this.esc(c.new_co)} · <span class="num">${c.tasks_moved || 0}</span> moved, <span class="num">${c.tasks_flagged || 0}</span> flagged <span class="tk-dc-note">${this.esc(String(c.at || '').slice(0, 10))}</span></div>`).join('')}
          <div class="tk-dc-note">Moved = the task was linked to the reservation and due exactly on the old date. Flagged (⚠ on the row) = same property &amp; date but not linked — check it; editing the due date clears the flag.</div>
        </div>` : ''}
      </div>` : '';
    // The board's columns are the status, so the status select only shows for rows.
    return `
      ${dcBanner}
      ${this.kpiHtml(kpiRows, board)}
      <div class="tk-filters">
        ${board ? '' : `<select class="input" id="tk-f-status" title="Status">${statusOpts}</select>`}
        <select class="input" id="tk-f-assignee" title="Assignee">${assigneeOpts}</select>
        <select class="input" id="tk-f-priority" title="Priority">${priorityOpts}</select>
        <select class="input" id="tk-f-listing" title="Property">${listingOpts}</select>
        <select class="input" id="tk-f-sort" title="Sort open tasks" aria-label="Sort">${SORT_MODES.map(m => `<option value="${m}" ${this.sortMode === m ? 'selected' : ''}>Sort: ${SORT_LABELS[m]}</option>`).join('')}</select>
        <label class="tk-search">
          ${icon('search', 15)}
          <input class="input" id="tk-f-search" type="text" placeholder="Search titles…" autocomplete="off" value="${this.esc(this.fSearch)}">
        </label>
      </div>
      ${cols ? this.boardHtml(cols) : `
      <div class="tk-list">
        <div class="tk-list-h"><span>Task</span><span>Assignees</span><span>Due</span><span class="tk-h-meta">Progress</span><span>Status</span></div>
        ${rows.length ? rows.map(t => this.rowHtml(t)).join('') : `<div class="tk-empty">No tasks match these filters.</div>`}
      </div>`}`;
  }

  // KPI band — computed from the FILTERED set so it answers "what am I looking at".
  // `board`: rows are the cards placed in the columns, so "of M in view" (the row
  // list's denominator) is dropped — the open count IS the three open columns.
  private kpiHtml(rows: any[], board = false): string {
    const today = this.localToday();
    const weekAgo = this.daysAgoStr(6);
    const open = rows.filter(t => OPEN_STATUSES.includes(t.status));
    const overdue = open.filter(t => t.due_date && t.due_date < today).length;
    const dueToday = open.filter(t => t.due_date === today).length;
    const unassigned = open.filter(t => this.taskAssignees(t).length === 0).length;
    const doneWeek = rows.filter(t => t.status === 'done' && String(t.completed_at || '').slice(0, 10) >= weekAgo).length;
    // R111: main.css .kpi-card (icon square + value + caption) at the Calendar's density.
    const card = (label: string, ico: string, v: number, cap: string, cls = '') => `
      <div class="kpi-card ${cls}">
        <div class="kpi-card-top"><span class="kpi-card-label">${label}</span><span class="kpi-card-ic">${icon(ico, 17)}</span></div>
        <div class="kpi-card-value">${v}</div>
        <div class="kpi-card-cap">${cap}</div>
      </div>`;
    return `
      <div class="kpi-grid tk-kpis">
        ${card('Open', 'target', open.length, board ? 'on the board' : `of ${rows.length} task${rows.length === 1 ? '' : 's'} in view`)}
        ${card('Overdue', 'clock', overdue, overdue > 0 ? 'past due and still open' : 'nothing past due', overdue > 0 ? 'tone-bad' : '')}
        ${card('Due today', 'calendar', dueToday, this.fmtDate(today))}
        ${card('Unassigned', 'users', unassigned, 'open, nobody on it')}
        ${card('Done this week', 'star', doneWeek, 'completed in the last 7 days')}
      </div>`;
  }

  private rowHtml(t: any): string {
    const closed = !OPEN_STATUSES.includes(t.status);
    const statusOpts = STATUSES.map(([k, l]) => `<option value="${k}" ${t.status === k ? 'selected' : ''}>${l}</option>`).join('');
    return `
      <div class="tk-row ${closed ? 'closed' : ''}" data-id="${this.esc(t.id)}">
        <div class="tk-row-main">
          <div class="tk-row-title" title="${this.esc(t.title)}">${this.esc(t.title)}</div>
          <div class="tk-row-sub">
            ${this.priorityPill(t.priority)}
            ${this.propertyChip(t)}
            ${t.blocked_by ? `<span class="pill pill-amber">blocked</span>` : ''}
            ${t.date_alert ? `<span class="pill pill-amber" title="${this.esc(t.date_alert.note || '')}">⚠ dates changed</span>` : ''}
            ${this.tourDifferPill(t)}
          </div>
        </div>
        <div class="tk-row-avs">${this.avatarStack(this.taskAssignees(t))}</div>
        <div class="tk-row-due">${this.duePill(t)}</div>
        <div class="tk-row-meta">${this.metaHtml(t)}</div>
        <select class="input tk-status" data-status="${this.esc(t.id)}" title="Status">${statusOpts}</select>
      </div>`;
  }

  // ── BOARD layout ──────────────────────────────────────────────
  // rows = filtered() without the status filter. Done shows the last two weeks
  // (done + canceled, newest first, capped) — older ones stay in the row list.
  // Column placement is its own step so the KPI band above the board counts
  // exactly the cards in it (R112 review).
  private boardColumns(rows: any[]): BoardColumn[] {
    const since = this.daysAgoStr(BOARD_DONE_DAYS - 1);
    const closedAt = (t: any) => String(t.completed_at || t.updated_at || t.created_at || '');
    return BOARD_COLS.map(([key, label]) => {
      if (key === 'done') {
        const all = rows.filter(t => !OPEN_STATUSES.includes(t.status) && closedAt(t).slice(0, 10) >= since)
          .sort((a, b) => closedAt(b).localeCompare(closedAt(a)));
        const items = all.slice(0, BOARD_DONE_CAP);
        return { key, label, items, more: all.length - items.length, sub: `last ${BOARD_DONE_DAYS} days`, pool: all };
      }
      return { key, label, items: rows.filter(t => t.status === key), more: 0, sub: '' };
    });
  }
  private boardHtml(cols: BoardColumn[]): string {
    const html = cols.map(({ key, label, items, more, sub }) => `
        <div class="tk-col" data-colkey="${key}">
          <div class="tk-col-h"><span class="tk-col-dot"></span>${label}${sub ? ` <span class="tk-col-sub">· ${sub}</span>` : ''}<span class="tk-col-n">${items.length}</span></div>
          <div class="tk-col-body" data-col="${key}">
            ${items.length ? items.map(t => this.boardCardHtml(t)).join('') : `<div class="tk-col-empty">Drop a card here</div>`}
            ${more > 0 ? `<div class="tk-col-more">+${more} more in the row list</div>` : ''}
          </div>
        </div>`).join('');
    return `<div class="tk-board">${html}</div>`;
  }

  private boardCardHtml(t: any): string {
    const closed = !OPEN_STATUSES.includes(t.status);
    return `
      <div class="tk-bcard ${this.isOverdue(t) ? 'overdue' : ''} ${closed ? 'closed' : ''} ${t.status === 'canceled' ? 'canceled' : ''}" data-id="${this.esc(t.id)}" draggable="true" title="Drag to another column to change status">
        <div class="tk-bcard-t">${this.esc(t.title)}</div>
        <div class="tk-bcard-sub">
          ${this.priorityPill(t.priority)}
          ${this.propertyChip(t)}
          ${t.status === 'canceled' ? `<span class="pill tk-pill-quiet">Canceled</span>` : ''}
          ${t.blocked_by ? `<span class="pill pill-amber">blocked</span>` : ''}
          ${t.date_alert ? `<span class="pill pill-amber" title="${this.esc(t.date_alert.note || '')}">⚠ dates</span>` : ''}
          ${this.tourDifferPill(t)}
        </div>
        <div class="tk-bcard-f">
          ${this.duePill(t)}
          ${this.metaHtml(t)}
          ${this.avatarStack(this.taskAssignees(t))}
        </div>
      </div>`;
  }

  // ── TASK PANEL (right overlay) ────────────────────────────────
  private checklistHtml(ck: any[]): string {
    // R104 #8: each item expands to description / instructions / sub-items
    return ck.map((it: any, i: number) => {
      const key = String(it.key || i);
      const open = this.ckOpen.has(key);
      const sub: any[] = Array.isArray(it.sub) ? it.sub : [];
      const subDone = sub.filter(x => x.done).length;
      const hasMeta = !!(it.description || it.instructions || sub.length);
      const subHtml = sub.map((x: any, j: number) => `
          <div class="tk-ck-item tk-ck-sub ${x.done ? 'done' : ''}">
            <button type="button" class="tk-ck-box" data-cks-toggle="${i}.${j}" title="${x.done ? 'Mark not done' : 'Mark done'}">✓</button>
            <span class="tk-ck-title">${this.esc(x.title)}</span>
            <button type="button" class="tk-ck-x" data-cks-del="${i}.${j}" title="Remove sub-item">✕</button>
          </div>`).join('');
      return `
      <div class="tk-ck-item ${it.done ? 'done' : ''}">
        <button type="button" class="tk-ck-box" data-ck-toggle="${i}" title="${it.done ? 'Mark not done' : 'Mark done'}">✓</button>
        <span class="tk-ck-title">${this.esc(it.title)}${sub.length ? ` <span class="tk-ck">${subDone}/${sub.length}</span>` : ''}${!open && it.description ? `<div class="tk-ck-hint">${this.esc(String(it.description).slice(0, 90))}</div>` : ''}</span>
        <button type="button" class="tk-ck-exp ${hasMeta ? 'has' : ''}" data-ck-open="${this.esc(key)}" title="${open ? 'Collapse' : 'Description, instructions, sub-items'}">${open ? '▾' : hasMeta ? '▸ details' : '▸'}</button>
        <button type="button" class="tk-ck-x" data-ck-del="${i}" title="Remove item">✕</button>
      </div>
      ${open ? `
      <div class="tk-ck-detail">
        <label>Description<textarea class="input" data-ck-desc="${i}" placeholder="What / why…">${this.esc(it.description || '')}</textarea></label>
        <label>Instructions<textarea class="input" data-ck-instr="${i}" placeholder="How — steps, tools, where things are…">${this.esc(it.instructions || '')}</textarea></label>
        <div class="tk-ck-sublist">${subHtml || '<div class="tk-ck-none">No sub-items yet.</div>'}</div>
        <div class="tk-ck-addrow">
          <input type="text" class="input" data-cks-add="${i}" data-ck-key="${this.esc(key)}" placeholder="Add sub-item… (Enter)" value="${this.esc(this.ckSubText[key] || '')}">
          <button type="button" class="btn btn-secondary btn-sm" data-cks-add-btn="${i}">Add</button>
        </div>
      </div>` : ''}`;
    }).join('');
  }

  private drawerHtml(): string {
    const d = this.draft;
    const live = this.taskById(this.drawerId);           // server truth for comments/log
    const ck = Array.isArray(d.checklist) ? d.checklist : [];
    const ckp = this.ckProgress(ck);
    const pct = ckp.total ? Math.round((ckp.done / ckp.total) * 100) : 0;
    const me = TasksPage.profileEmail();
    const comments = ((live && live.comments) || []).map((c: any) => `
      <div class="tk-comment">
        ${this.avatar(String(c.by || ''), 'lg')}
        <div class="tk-comment-b">
          <div class="tk-comment-h"><b>${this.esc(this.personName(c.by) || c.by || '—')}</b><span>${this.esc(this.fmtAt(c.at))}</span></div>
          <div class="tk-comment-t">${renderWithMentions(this.esc(c.text), this.mentionPeople)}</div>
          ${(c.photos || []).map((u: string) => `<a href="${this.esc(u)}" target="_blank" rel="noopener"><img src="${this.esc(u)}" alt="" loading="lazy" decoding="async"></a>`).join('')}
        </div>
      </div>`).join('');
    // R115 C2: the Activity timeline — every change, newest first (20 until "Show all").
    // R120 (D32): Changes | Comments | All — the comment lines repeat the
    // Comments card, so Changes (the default) leaves them out.
    const logAll: any[] = ((live && live.log) || []).slice().reverse();
    const isComment = (l: any) => String((l && l.action) || '') === 'comment';
    const nComments = logAll.filter(isComment).length;
    const nOf: Record<LogFilter, number> = { changes: logAll.length - nComments, comments: nComments, all: logAll.length };
    const logFiltered = this.logFilter === 'all' ? logAll : logAll.filter(l => (this.logFilter === 'comments') === isComment(l));
    const logShown = this.logAll ? logFiltered : logFiltered.slice(0, 20);
    const log = logShown.map((l: any) => this.logItemHtml(l)).join('');
    // R120: a task older than field-level logging says so, instead of looking complete.
    const preCutoff = !!live && !!live.created_at && String(live.created_at) < LOG_FULL_SINCE;
    const src = this.sourceLink(live || d);
    const nIssues = live && live.source_ref && Array.isArray(live.source_ref.board_ids) ? live.source_ref.board_ids.length : 0;
    const canOpenBoard = TasksPage.fieldRole() !== 'cleaner';
    return `
      <div class="tk-overlay" id="tk-drawer-backdrop">
        <div class="tk-drawer" role="dialog" aria-label="Task">
          <div class="tk-panel-head">
            <div class="tk-panel-eyebrow">
              <span class="tk-eyebrow">Task</span>
              <span class="tk-meta-txt">${live ? `created ${this.esc(this.fmtAt(live.created_at))}${live.created_by ? ` by ${this.esc(this.personName(live.created_by))}` : ''}` : ''}</span>
              ${nIssues ? `<span class="pill pill-blue" title="Finishing the task resolves ${nIssues === 1 ? 'it' : 'them'} on the Issues board">📋 ${nIssues} issue${nIssues === 1 ? '' : 's'}</span>` : ''}
              ${live && live.date_alert ? `<span class="pill pill-amber" title="${this.esc(live.date_alert.note || '')}">⚠ dates changed</span>` : ''}
              <button type="button" class="tk-x" id="tk-d-close" title="Close">✕</button>
            </div>
            <input type="text" class="tk-title-in" data-dfield="title" value="${this.esc(d.title)}" placeholder="Task title" aria-label="Title">
            <div class="tk-panel-segs">
              <span class="tk-seg-lab">Status</span>${this.segHtml('data-dstatus', STATUSES, d.status)}
              <span class="tk-seg-lab">Priority</span>${this.segHtml('data-dpriority', PRIORITIES, d.priority || 'normal')}
            </div>
          </div>
          <div class="tk-drawer-body">
            <div class="tk-panel-grid">
              <div class="tk-pane-main">
                <div class="tk-sec">
                  <div class="tk-sec-head"><h4>Description</h4></div>
                  <div class="tk-mwrap"><textarea class="input tk-desc" data-dfield="description" placeholder="What needs doing, and anything the person needs to know… (type @ to mention someone)">${this.esc(d.description)}</textarea></div>
                </div>
                <div class="tk-sec">
                  <div class="tk-sec-head">
                    <h4>Checklist</h4>
                    ${ckp.total ? `<span class="tk-sec-n">${ckp.done}/${ckp.total}</span><div class="tk-prog ${ckp.done >= ckp.total ? 'full' : ''}" title="${pct}% done"><i style="width:${pct}%"></i></div>` : ''}
                  </div>
                  ${this.checklistHtml(ck) || '<div class="tk-muted-note">No checklist items yet — add the steps so anyone can pick this up.</div>'}
                  <div class="tk-ck-addrow">
                    <input type="text" class="input" id="tk-d-ck-add" placeholder="Add checklist item… (Enter)" value="${this.esc(this.ckText)}">
                    <button type="button" class="btn btn-secondary btn-sm" id="tk-d-ck-btn">Add</button>
                  </div>
                </div>
                <div class="tk-sec">
                  <div class="tk-sec-head"><h4>Comments</h4>${live && live.comments && live.comments.length ? `<span class="tk-sec-n">${live.comments.length}</span>` : ''}</div>
                  ${comments || '<div class="tk-muted-note">No comments yet.</div>'}
                  <div class="tk-c-compose">
                    ${me ? this.avatar(me, 'lg') : ''}
                    <div class="tk-c-box">
                      <div class="tk-mwrap up"><textarea class="input" id="tk-d-comment" placeholder="Add a comment… type @ to mention someone (they're notified)">${this.esc(this.commentText)}</textarea></div>
                      <div class="tk-c-actions">
                        ${this.commentFiles.length
                          ? `<span class="tk-c-picked">📷 ${this.commentFiles.length === 1 ? this.esc(this.commentFiles[0].name) : `${this.commentFiles.length} photos`} <button type="button" class="tk-linkbtn" id="tk-d-cfile-clear" title="Remove photos">✕</button></span>`
                          : `<label class="btn btn-secondary btn-sm" title="Attach photos">📷 Photo<input type="file" accept="image/*" multiple id="tk-d-cfile" style="display:none;"></label>`}
                        <button type="button" class="btn btn-primary btn-sm tk-send" id="tk-d-send">Comment</button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div class="tk-pane-side">
                ${live ? this.tourBlockHtml(live) : ''}
                <div class="tk-sec">
                  <div class="tk-sec-head"><h4>Details</h4></div>
                  <div class="tk-kv">
                    <div class="tk-kv-row"><span class="tk-kv-k">Status</span><span class="tk-kv-v"><select class="input" data-dfield="status">${STATUSES.map(([k, l]) => `<option value="${k}" ${d.status === k ? 'selected' : ''}>${l}</option>`).join('')}</select></span></div>
                    <div class="tk-kv-row"><span class="tk-kv-k">Priority</span><span class="tk-kv-v"><select class="input" data-dfield="priority">${PRIORITIES.map(([k, l]) => `<option value="${k}" ${(d.priority || 'normal') === k ? 'selected' : ''}>${l}</option>`).join('')}</select></span></div>
                    <div class="tk-kv-row"><span class="tk-kv-k">Due</span><span class="tk-kv-v tk-due-pair"><input type="date" class="input" data-dfield="due_date" value="${this.esc(d.due_date || '')}" aria-label="Due date"><input type="time" class="input" data-dfield="due_time" step="900" value="${this.esc(d.due_time || '')}" aria-label="Due time (optional)" title="Time (optional)"></span></div>
                    <div class="tk-kv-row top"><span class="tk-kv-k">Assignees</span><span class="tk-kv-v">${this.assigneePicker(this.taskAssignees(d), 'data-dassignees', this.asQuery)}</span></div>
                    <div class="tk-kv-row top"><span class="tk-kv-k" title="Watchers hear about changes and comments, like assignees">Watchers</span><span class="tk-kv-v">${this.assigneePicker(this.taskWatchers(d), 'data-dwatchers', this.wsQuery, 'Add watchers…')}</span></div>
                    <div class="tk-kv-row top"><span class="tk-kv-k">Property</span><span class="tk-kv-v">${this.listingInput('data-dlisting', 'tk-d-listing-dl', this.listingTyped, this.listingBad)}</span></div>
                    ${live ? `<div class="tk-kv-row"><span class="tk-kv-k">Created</span><span class="tk-kv-v muted">${this.esc(this.fmtAt(live.created_at))}${live.created_by ? ` · ${this.esc(this.personName(live.created_by))}` : ''}</span></div>` : ''}
                    ${live && live.updated_at ? `<div class="tk-kv-row"><span class="tk-kv-k">Updated</span><span class="tk-kv-v muted">${this.esc(this.fmtAt(live.updated_at))}</span></div>` : ''}
                    ${live && live.completed_at ? `<div class="tk-kv-row"><span class="tk-kv-k">Completed</span><span class="tk-kv-v muted">${this.esc(this.fmtAt(live.completed_at))}</span></div>` : ''}
                    ${src ? `<div class="tk-kv-row"><span class="tk-kv-k">Source</span><span class="tk-kv-v">${((src.kind === 'board' && !canOpenBoard) || (src.kind === 'mtr_lead' && !this.canOpenMtr())) ? `<span class="muted">${this.esc(src.kind === 'mtr_lead' ? 'MTR tour' : src.label)}</span>` : `<button type="button" class="tk-kv-link" data-tk-source="${this.esc(src.kind)}" data-tk-source-id="${this.esc(src.id)}" title="Open ${this.esc(src.label.toLowerCase())}">${this.esc(src.label)} ▸</button>`}</span></div>` : ''}
                  </div>
                </div>
                <div class="tk-sec">
                  <details class="tk-details" id="tk-d-activity" ${logAll.length && !this.logClosed ? 'open' : ''}>
                    <summary>Activity (${logAll.length})</summary>
                    ${logAll.length ? `<div class="seg-tabs tk-log-f" role="tablist" aria-label="Show in the activity">${LOG_FILTERS.map(([k, l]) => `<button type="button" class="seg-tab ${this.logFilter === k ? 'active' : ''}" data-tk-log-f="${k}" aria-pressed="${this.logFilter === k}">${l} <span class="tk-log-n">${nOf[k]}</span></button>`).join('')}</div>` : ''}
                    ${log ? `<ol class="tk-tl">${log}</ol>` : `<div class="tk-muted-note tk-details-body">${!logAll.length ? 'No activity recorded.' : this.logFilter === 'comments' ? 'No comments yet.' : 'No changes recorded yet.'}</div>`}
                    ${logFiltered.length > 20 ? `<button type="button" class="tk-tl-more" data-tk-log-all>${this.logAll ? 'Show the latest 20' : `Show all ${logFiltered.length}`}</button>` : ''}
                    ${preCutoff ? `<div class="tk-tl-foot">Detailed change history starts Oct 15, 2026 — earlier edits other than status weren’t recorded.</div>` : ''}
                  </details>
                </div>
              </div>
            </div>
          </div>
          <div class="tk-drawer-foot">
            ${this.isOffice() ? `<button type="button" class="btn btn-outline tk-danger" id="tk-d-delete">Delete</button>` : ''}
            <button type="button" class="btn btn-secondary" id="tk-d-close2">Cancel</button>
            <button type="button" class="btn btn-primary" id="tk-d-save" ${this.listingBad ? 'disabled' : ''}>Save</button>
          </div>
          ${this.closeAsk ? this.closeAskHtml() : ''}
        </div>
      </div>`;
  }
  // R120: ✕ / backdrop with unsaved edits — ask instead of silently dropping them.
  private closeAskHtml(): string {
    const words = [...new Set(this.unsavedKeys().map(k => FIELD_WORD[k] || k.replace(/_/g, ' ')))];
    const list = words.length > 1 ? `${words.slice(0, -1).join(', ')} and ${words[words.length - 1]}` : (words[0] || 'this task');
    return `
      <div class="tk-ask" id="tk-ask" role="alertdialog" aria-modal="true" aria-labelledby="tk-ask-h">
        <div class="tk-ask-card">
          <div class="tk-ask-h" id="tk-ask-h">Save changes before closing?</div>
          <div class="tk-ask-t">You changed the ${this.esc(list)} and haven’t saved.</div>
          <div class="tk-ask-acts">
            <button type="button" class="btn btn-ghost" id="tk-ask-keep">Keep editing</button>
            <button type="button" class="btn btn-secondary" id="tk-ask-discard">Discard</button>
            <button type="button" class="btn btn-primary" id="tk-ask-save">Save</button>
          </div>
        </div>
      </div>`;
  }

  // R116 #7: the MTR tour this task is for — its day, the "dates differ" fix
  // (office: move the tour; anyone: set the task back) and what Done does.
  private tourBlockHtml(t: any): string {
    const tr = this.tourOf(t);
    if (!tr) return '';
    const tourDay = tr.tour_at.slice(0, 10), tourTime = tr.tour_at.slice(11, 16);
    const st = tr.status === 'deleted' ? 'Tour deleted' : tr.status === 'no_show' ? 'No-show' : tr.status === 'cancelled' ? 'Cancelled' : tr.status === 'done' ? 'Done' : 'Scheduled';
    const tone = tr.status === 'done' ? 'pill-green' : tr.status === 'scheduled' ? 'pill-blue' : 'tk-pill-quiet';
    const office = this.canOpenMtr();
    const moveTo = tr.differ ? `${t.due_date}T${t.due_time || tourTime}` : '';
    return `<div class="tk-sec tk-tour">
      <div class="tk-sec-head"><h4>MTR tour</h4><span class="pill ${tone}">${this.esc(st)}</span></div>
      <div class="tk-tour-when">🗓 ${this.esc(this.fmtDate(tourDay))} · ${this.esc(this.fmtTime(tourTime))}${t.listing_name ? ` <span class="tk-tour-home">· ${this.esc(t.listing_name)}</span>` : ''}</div>
      ${tr.differ ? `<div class="tk-tour-differ" role="note">
        <div>⚠ <b>Dates differ</b> — this task is due ${this.esc(this.fmtDate(t.due_date))}, the tour is still ${this.esc(this.fmtDate(tourDay))}. The tour never moves on its own.</div>
        <div class="tk-tour-acts">
          ${office ? `<button type="button" class="btn btn-secondary btn-sm" data-tk-tour-move="${this.esc(moveTo)}">Move tour to ${this.esc(this.fmtDate(t.due_date))}</button>` : ''}
          <button type="button" class="btn ${office ? 'btn-ghost' : 'btn-secondary'} btn-sm" data-tk-tour-back>Set the task back to ${this.esc(this.fmtDate(tourDay))}</button>
        </div>
        ${office ? '' : '<div class="tk-tour-hint">Only the office can move the tour itself — ask them, or set the task back.</div>'}
      </div>` : ''}
      ${tr.status === 'scheduled' ? `<div class="tk-tour-hint">Marking this task <b>Done</b> after the showing marks the tour done on the lead (from the tour day on).</div>` : ''}
      ${office ? `<button type="button" class="tk-kv-link" data-tk-source="mtr_lead" data-tk-source-id="${this.esc(tr.lead_id)}">Open the lead ▸</button>` : ''}
    </div>`;
  }
  private async moveTourFromTask(at: string): Promise<void> {
    const live = this.taskById(this.drawerId);
    const tr = live ? this.tourOf(live) : null;
    if (!live || !tr || !/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(at)) return;
    let r: Response | null = null;
    try { r = await fetch(`/api/mtr/leads/${encodeURIComponent(tr.lead_id)}/tours/${encodeURIComponent(tr.tour_id)}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ at }) }); }
    catch { alert('No connection — the tour was not moved.'); return; }
    const j = await r.json().catch(() => ({} as any));
    if (!r.ok) { alert(j.error || `Couldn’t move the tour (${r.status}).`); return; }
    // the server moved the task with the tour — take its fresh copy
    const all = await fetch(API).then(x => (x.ok ? x.json() : null)).catch(() => null);
    const fresh = all && Array.isArray(all.tasks) ? all.tasks.find((x: any) => x.id === live.id) : null;
    if (fresh) { this.adoptServerCopy(fresh); this.foldAnswer(fresh); }
    TasksPage.changed();
    this.note = `Tour moved to ${this.fmtDate(at.slice(0, 10))} ${this.fmtTime(at.slice(11, 16))} — the task and the tour match again.${j.warnings && j.warnings.length ? ` Heads-up: ${j.warnings[0].text}` : ''}`;
    this.draw();
  }

  // ── CREATE modal ──────────────────────────────────────────────
  private blankDraft(): any {
    return { title: '', description: '', status: 'todo', priority: 'normal', assignee: null, assignees: [], due_date: null, due_time: null, listing_id: null, listing_name: '' };
  }
  // Back-compat read: a task's assignees as an array (new field, else the legacy
  // single assignee).
  private taskAssignees(t: any): string[] {
    if (Array.isArray(t.assignees) && t.assignees.length) return t.assignees;
    return t.assignee ? [t.assignee] : [];
  }
  // R115: watchers[] (stored since R18, first surfaced now) — notified like assignees.
  private taskWatchers(t: any): string[] { return Array.isArray(t.watchers) ? t.watchers.filter(Boolean) : []; }
  // R112: searchable multi-select (replaces the wall of chips) — selected
  // people as initials chips with ✕, a search box, and a filtered menu of
  // `.tk-assignee-chip[data-assignee-toggle](.active)` rows (same hook/class
  // language as before). The outer `[data-dassignees|data-cassignees]` box is
  // re-rendered IN PLACE by bindAssigneePicker, never via a full redraw.
  // The search <input> is a PERSISTENT element: a full draw() rebuilds it from
  // this.asQuery / cAsQuery, but the in-place picker only ever swaps the chips
  // and the menu around it (replacing a focused input mid-event recursed).
  private assigneePicker(selected: string[], attr: string, query: string, placeholder = ''): string {
    const q = query.trim();
    return `<div class="tk-as" ${attr}>
      <div class="tk-as-sel">${this.assigneeSelHtml(selected, placeholder ? 'No watchers' : '')}</div>
      <div class="tk-as-search">${icon('search', 13)}<input type="text" class="input" data-assignee-search placeholder="${this.people.length ? (placeholder || `Add people… (${this.people.length})`) : 'No people available'}" value="${this.esc(query)}" autocomplete="off"></div>
      <div class="tk-as-menu" ${q ? '' : 'hidden'}>${this.assigneeMenuHtml(selected, query)}</div>
    </div>`;
  }
  private assigneeSelHtml(selected: string[], none = ''): string {
    return (selected || []).map(e => `
      <span class="tk-as-chip">${this.avatar(e)}<span class="tk-as-name">${this.esc(this.personName(e) || e)}</span><button type="button" class="tk-as-rm" data-assignee-remove="${this.esc(e)}" title="Remove">✕</button></span>`).join('')
      || `<span class="tk-as-none">${this.esc(none || 'Nobody yet — search below')}</span>`;
  }
  private assigneeMenuHtml(selected: string[], query: string): string {
    const sel = new Set((selected || []).map(s => String(s).toLowerCase()));
    const q = query.trim().toLowerCase();
    const matches = this.people.filter(p => !q || String(p.full_name || '').toLowerCase().includes(q) || String(p.email || '').toLowerCase().includes(q) || String(p.role || '').toLowerCase().includes(q));
    return matches.map(p => {
      const on = sel.has(String(p.email).toLowerCase());
      return `<button type="button" class="tk-assignee-chip ${on ? 'active' : ''}" data-assignee-toggle="${this.esc(p.email)}">${this.avatar(String(p.email))}<span class="tk-as-mname">${this.esc(p.full_name || p.email)}</span><span class="tk-as-role">${this.esc(String(p.role || '').replace(/_/g, ' '))}</span>${on ? '<span class="tk-as-check">✓</span>' : ''}</button>`;
    }).join('') || `<div class="tk-as-none">${this.people.length ? 'No one matches' : 'No people available'}</div>`;
  }
  private createHtml(): string {
    const d = this.cDraft;
    return `
      <div class="tk-overlay center" id="tk-create-backdrop">
        <div class="tk-modal" role="dialog" aria-label="New task">
          <div class="tk-panel-head">
            <div class="tk-panel-eyebrow">
              <span class="tk-eyebrow">New task</span>
              <span class="tk-meta-txt" title="Assignees and anyone you @mention are notified in the app (and on Slack once it's connected).">Assignees and anyone you @mention are notified in the app (and on Slack once it's connected).</span>
              <button type="button" class="tk-x" id="tk-c-cancel" title="Close">✕</button>
            </div>
            <input type="text" class="tk-title-in" data-cfield="title" value="${this.esc(d.title)}" placeholder="What needs doing?" aria-label="Title">
            <div class="tk-panel-segs">
              <span class="tk-seg-lab">Status</span>${this.segHtml('data-cstatus', STATUSES.filter(([k]) => k !== 'canceled'), d.status || 'todo')}
              <span class="tk-seg-lab">Priority</span>${this.segHtml('data-cpriority', PRIORITIES, d.priority || 'normal')}
            </div>
          </div>
          <div class="tk-drawer-body">
            <div class="tk-panel-grid">
              <div class="tk-pane-main">
                <div class="tk-sec">
                  <div class="tk-sec-head"><h4>Description</h4></div>
                  <div class="tk-mwrap"><textarea class="input tk-desc" data-cfield="description" placeholder="What needs doing, and anything the person needs to know… (type @ to mention someone)">${this.esc(d.description)}</textarea></div>
                </div>
              </div>
              <div class="tk-pane-side">
                <div class="tk-sec">
                  <div class="tk-sec-head"><h4>Details</h4></div>
                  <div class="tk-kv">
                    <div class="tk-kv-row"><span class="tk-kv-k">Priority</span><span class="tk-kv-v"><select class="input" data-cfield="priority">${PRIORITIES.map(([k, l]) => `<option value="${k}" ${d.priority === k ? 'selected' : ''}>${l}</option>`).join('')}</select></span></div>
                    <div class="tk-kv-row"><span class="tk-kv-k">Due</span><span class="tk-kv-v tk-due-pair"><input type="date" class="input" data-cfield="due_date" value="${this.esc(d.due_date || '')}" aria-label="Due date"><input type="time" class="input" data-cfield="due_time" step="900" value="${this.esc(d.due_time || '')}" aria-label="Due time (optional)" title="Time (optional)"></span></div>
                    <div class="tk-kv-row top"><span class="tk-kv-k">Assignees</span><span class="tk-kv-v">${this.assigneePicker(d.assignees || (d.assignee ? [d.assignee] : []), 'data-cassignees', this.cAsQuery)}</span></div>
                    <div class="tk-kv-row top"><span class="tk-kv-k">Property</span><span class="tk-kv-v">${this.listingInput('data-clisting', 'tk-c-listing-dl', this.cListingTyped, this.cListingBad)}</span></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="tk-drawer-foot">
            <button type="button" class="btn btn-secondary" id="tk-c-cancel2">Cancel</button>
            <button type="button" class="btn btn-primary" id="tk-c-save" ${this.cListingBad ? 'disabled' : ''}>Create task</button>
          </div>
        </div>
      </div>`;
  }

  // ── bind (re-attached after every draw) ───────────────────────
  private bind(): void {
    const body = this.container;

    // R57: turnover cards open the cleaner's Housekeeping job view.
    body.querySelectorAll<HTMLElement>('[data-tk-gohk]').forEach(el => el.addEventListener('click', () => {
      document.dispatchEvent(new CustomEvent('app:navigate', { detail: 'housekeeping' }));
    }));

    // header
    body.querySelectorAll<HTMLElement>('[data-tk-dc-toggle]').forEach(el => el.addEventListener('click', () => {
      this.dcOpen = !this.dcOpen;
      this.draw();
    }));
    body.querySelectorAll<HTMLElement>('[data-view]').forEach(el => el.addEventListener('click', () => {
      this.view = el.dataset.view as 'my' | 'list';
      this.draw();
    }));
    body.querySelectorAll<HTMLElement>('[data-layout]').forEach(el => el.addEventListener('click', () => {
      this.layout = el.dataset.layout === 'board' ? 'board' : 'list';
      try { localStorage.setItem(LAYOUT_KEY, this.layout); } catch { /* private mode */ }
      this.draw();
    }));
    body.querySelector<HTMLButtonElement>('#tk-new')?.addEventListener('click', () => {
      this.cDraft = this.blankDraft();
      this.cListingTyped = ''; this.cListingBad = false;   // R104 #7
      this.cAsQuery = '';
      this.showCreate = true;
      this.draw();
    });

    // list filters
    const fSel = (id: string, set: (v: string) => void) => {
      const el = body.querySelector<HTMLSelectElement>(id);
      el?.addEventListener('change', () => { set(el.value); this.draw(); });
    };
    fSel('#tk-f-status', v => { this.fStatus = v; });
    fSel('#tk-f-assignee', v => { this.fAssignee = v; });
    fSel('#tk-f-priority', v => { this.fPriority = v; });
    fSel('#tk-f-listing', v => { this.fListing = v; });
    fSel('#tk-f-sort', v => {   // R120: remembered per browser (blocked storage → just this visit)
      this.sortMode = normSortMode(v);
      try { localStorage.setItem(SORT_KEY, this.sortMode); } catch { /* private mode */ }
    });
    const search = body.querySelector<HTMLInputElement>('#tk-f-search');
    search?.addEventListener('input', () => {
      this.fSearch = search.value;
      this.searchCaret = search.selectionStart ?? search.value.length;  // keep the caret where the user is typing
      this.searchFocus = true;
      this.draw();
    });
    if (this.searchFocus) {
      const el = body.querySelector<HTMLInputElement>('#tk-f-search');
      if (el) { el.focus(); const n = Math.min(this.searchCaret, el.value.length); el.setSelectionRange(n, n); }
      this.searchFocus = false;
    }

    // list rows — click opens the panel; the inline status select posts.
    body.querySelectorAll<HTMLElement>('.tk-row[data-id]').forEach(el => el.addEventListener('click', (e) => {
      if ((e.target as HTMLElement).closest('select')) return;
      this.openDrawer(el.dataset.id!);
    }));
    body.querySelectorAll<HTMLSelectElement>('select[data-status]').forEach(sel =>
      sel.addEventListener('change', () => void this.postStatus(sel.dataset.status!, sel.value)));

    // board cards — click opens the panel; drag between columns changes status.
    this.bindBoard(body);

    // my-work cards
    body.querySelectorAll<HTMLElement>('.tk-card[data-id]').forEach(el => el.addEventListener('click', (e) => {
      const t = e.target as HTMLElement;
      if (t.closest('button') || t.closest('input')) return;
      this.openDrawer(el.dataset.id!);
    }));
    body.querySelectorAll<HTMLButtonElement>('[data-done]').forEach(btn => btn.addEventListener('click', (e) => {
      e.stopPropagation();
      void this.postStatus(btn.dataset.done!, 'done');
    }));
    body.querySelectorAll<HTMLButtonElement>('[data-cam]').forEach(btn => btn.addEventListener('click', (e) => {
      e.stopPropagation();
      body.querySelector<HTMLInputElement>(`input[data-photo="${btn.dataset.cam}"]`)?.click();
    }));
    body.querySelectorAll<HTMLInputElement>('input[data-photo]').forEach(inp => inp.addEventListener('change', () =>
      void this.attachProof(inp.dataset.photo!, inp)));

    // panel
    if (this.drawerId && this.draft) this.bindDrawer(body);

    // create modal
    if (this.showCreate && this.cDraft) this.bindCreate(body);
  }

  // R112: HTML5 drag & drop between board columns. The card carries its id;
  // a drop on a different column posts the new status (optimistic move, reverted
  // on failure). Touch devices change status from the panel instead.
  private bindBoard(body: HTMLElement): void {
    body.querySelectorAll<HTMLElement>('.tk-bcard[data-id]').forEach(card => {
      card.addEventListener('click', (e) => {
        if ((e.target as HTMLElement).closest('button')) return;
        this.openDrawer(card.dataset.id!);
      });
      card.addEventListener('dragstart', (e) => {
        this.dragId = card.dataset.id!;
        card.classList.add('dragging');
        try { e.dataTransfer?.setData('text/plain', this.dragId); if (e.dataTransfer) e.dataTransfer.effectAllowed = 'move'; } catch { /* older engines */ }
      });
      card.addEventListener('dragend', () => {
        this.dragId = null;
        card.classList.remove('dragging');
        body.querySelectorAll('.tk-col-body.over').forEach(c => c.classList.remove('over'));
      });
    });
    body.querySelectorAll<HTMLElement>('.tk-col-body[data-col]').forEach(col => {
      col.addEventListener('dragover', (e) => { if (!this.dragId) return; e.preventDefault(); if (e.dataTransfer) e.dataTransfer.dropEffect = 'move'; col.classList.add('over'); });
      col.addEventListener('dragleave', (e) => { if (!col.contains(e.relatedTarget as Node)) col.classList.remove('over'); });
      col.addEventListener('drop', (e) => {
        e.preventDefault();
        col.classList.remove('over');
        const id = this.dragId || (() => { try { return e.dataTransfer?.getData('text/plain') || ''; } catch { return ''; } })();
        this.dragId = null;
        if (id) void this.moveTask(id, col.dataset.col!);
      });
    });
  }

  private bindDrawer(body: HTMLElement): void {
    const backdrop = body.querySelector<HTMLElement>('#tk-drawer-backdrop');
    // R120: ✕ and the backdrop ask first when there are unsaved edits; Cancel
    // stays the explicit "throw my edits away".
    backdrop?.addEventListener('click', (e) => { if (e.target === backdrop) this.requestClose(); });
    body.querySelector('#tk-d-close')?.addEventListener('click', () => this.requestClose());
    body.querySelector('#tk-d-close2')?.addEventListener('click', () => this.closeDrawer());
    body.querySelector('#tk-ask-keep')?.addEventListener('click', () => { this.closeAsk = false; this.draw(); });
    body.querySelector('#tk-ask-discard')?.addEventListener('click', () => this.closeDrawer());
    body.querySelector('#tk-ask-save')?.addEventListener('click', () => {
      this.closeAsk = false;
      const d = this.draft;
      // a blank title / unmatched property: the explicit Save says what's wrong and the panel stays open
      if (!d || !String(d.title || '').trim() || this.listingBad) { void this.saveDraft(); return; }
      void this.saveDraft();   // captured now — it lands even though the panel closes (R115 fix 2)
      this.closeDrawer();
    });

    // Every form input mirrors into this.draft on input/change — redraws
    // re-render from the draft, so edits are never lost. Status/priority also
    // keep the header segmented control in step (in place, no redraw).
    body.querySelectorAll<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>('[data-dfield]').forEach(el => {
      const f = el.dataset.dfield!;
      const apply = () => {
        if (!this.draft) return;
        const v: string = (el as any).value;
        if (f === 'assignee' || f === 'due_date' || f === 'due_time') {   // R116 #7: + due_time
          this.draft[f] = v || null;
        } else {
          this.draft[f] = v;
        }
        if (f === 'status') this.syncSeg(body, 'data-dstatus', v);
        if (f === 'priority') this.syncSeg(body, 'data-dpriority', v);
      };
      el.addEventListener('input', apply);
      el.addEventListener('change', apply);
    });
    this.bindSeg(body, 'data-dstatus', () => this.draft, 'status', '[data-dfield="status"]');
    this.bindSeg(body, 'data-dpriority', () => this.draft, 'priority', '[data-dfield="priority"]');
    this.bindListingTypeahead(body, 'data-dlisting', () => this.draft, v => { this.listingTyped = v; }, b => { this.listingBad = b; });   // R104 #7

    this.bindAssigneePicker(body.querySelector('[data-dassignees]'), () => this.draft, () => this.asQuery, v => { this.asQuery = v; });
    this.bindAssigneePicker(body.querySelector('[data-dwatchers]'), () => this.draft, () => this.wsQuery, v => { this.wsQuery = v; }, 'watchers');   // R115
    // R115 C1: '@' in the description → mention picker (the server notifies newly named people on Save).
    this.bindMentionPicker(body.querySelector<HTMLTextAreaElement>('[data-dfield="description"]'), v => { if (this.draft) this.draft.description = v; });
    // R115 C2: the Activity card remembers a collapse; "Show all" past 20.
    const act = body.querySelector<HTMLDetailsElement>('#tk-d-activity');
    act?.addEventListener('toggle', () => { this.logClosed = !act.open; });
    body.querySelector('[data-tk-log-all]')?.addEventListener('click', () => { this.logAll = !this.logAll; this.draw(); });
    // R120 (D32): Changes | Comments | All — remembered per browser
    body.querySelectorAll<HTMLButtonElement>('[data-tk-log-f]').forEach(btn => btn.addEventListener('click', () => {
      const f = btn.dataset.tkLogF as LogFilter;
      if (!LOG_FILTERS.some(([k]) => k === f) || f === this.logFilter) return;
      this.logFilter = f; this.logAll = false;
      try { localStorage.setItem(LOG_FILTER_KEY, f); } catch { /* private mode */ }
      this.draw();
    }));
    body.querySelector('#tk-d-save')?.addEventListener('click', () => void this.saveDraft());
    body.querySelector('#tk-d-delete')?.addEventListener('click', () => void this.deleteTask());
    body.querySelectorAll<HTMLButtonElement>('[data-tk-source]').forEach(btn => btn.addEventListener('click', () =>
      this.goSource(btn.dataset.tkSource!, btn.dataset.tkSourceId || '')));
    // R116 #7: "dates differ" — move the tour (office) or set the task back
    body.querySelector<HTMLButtonElement>('[data-tk-tour-move]')?.addEventListener('click', (e) => {
      const b = e.currentTarget as HTMLButtonElement; b.disabled = true;
      void this.moveTourFromTask(b.dataset.tkTourMove || '').finally(() => { b.disabled = false; });
    });
    body.querySelector<HTMLButtonElement>('[data-tk-tour-back]')?.addEventListener('click', () => {
      const live = this.taskById(this.drawerId);
      const tr = live ? this.tourOf(live) : null;
      if (!tr || !this.draft) return;
      this.draft.due_date = tr.tour_at.slice(0, 10);
      this.draft.due_time = tr.tour_at.slice(11, 16) || null;
      void this.saveDraft();
    });

    // checklist — mutations persist immediately (POST the changed checklist; R115 fix: never the whole task).
    const ckAdd = body.querySelector<HTMLInputElement>('#tk-d-ck-add');
    ckAdd?.addEventListener('input', () => { this.ckText = ckAdd.value; });
    ckAdd?.addEventListener('keydown', (e) => { if ((e as KeyboardEvent).key === 'Enter') { e.preventDefault(); void this.addChecklistItem(); } });
    body.querySelector('#tk-d-ck-btn')?.addEventListener('click', () => void this.addChecklistItem());
    body.querySelectorAll<HTMLButtonElement>('[data-ck-toggle]').forEach(btn => btn.addEventListener('click', () => {
      const i = Number(btn.dataset.ckToggle);
      if (this.draft && this.draft.checklist && this.draft.checklist[i]) {
        this.draft.checklist[i].done = !this.draft.checklist[i].done;
        this.draw();   // review R104-2 #5: render the tick now (a mid-flight keystroke can suppress the echo redraw)
        void this.saveDraft(true);
      }
    }));
    body.querySelectorAll<HTMLButtonElement>('[data-ck-del]').forEach(btn => btn.addEventListener('click', () => {
      const i = Number(btn.dataset.ckDel);
      if (this.draft && this.draft.checklist) {
        this.draft.checklist.splice(i, 1);
        this.draw();
        void this.saveDraft(true);
      }
    }));
    // R104 #8: expand/collapse (state-backed), description/instructions
    // (mirror on input, persist on change/blur), sub-items add/toggle/delete.
    body.querySelectorAll<HTMLButtonElement>('[data-ck-open]').forEach(btn => btn.addEventListener('click', () => {
      const k = btn.dataset.ckOpen!;
      if (this.ckOpen.has(k)) this.ckOpen.delete(k); else this.ckOpen.add(k);
      this.draw();
    }));
    const bindMeta = (attr: 'data-ck-desc' | 'data-ck-instr', field: 'description' | 'instructions') =>
      body.querySelectorAll<HTMLTextAreaElement>(`[${attr}]`).forEach(ta => {
        const i = Number(attr === 'data-ck-desc' ? ta.dataset.ckDesc : ta.dataset.ckInstr);
        ta.addEventListener('input', () => { const it = this.draft?.checklist?.[i]; if (it) it[field] = ta.value; });
        ta.addEventListener('change', () => void this.saveDraft(true));
      });
    bindMeta('data-ck-desc', 'description');
    bindMeta('data-ck-instr', 'instructions');
    body.querySelectorAll<HTMLInputElement>('[data-cks-add]').forEach(inp => {
      const key = inp.dataset.ckKey!;
      inp.addEventListener('input', () => { this.ckSubText[key] = inp.value; });
      inp.addEventListener('keydown', (e) => { if ((e as KeyboardEvent).key === 'Enter') { e.preventDefault(); void this.addSubItem(Number(inp.dataset.cksAdd)); } });
    });
    body.querySelectorAll<HTMLButtonElement>('[data-cks-add-btn]').forEach(btn =>
      btn.addEventListener('click', () => void this.addSubItem(Number(btn.dataset.cksAddBtn))));
    const subAt = (pair: string) => {
      const [i, j] = pair.split('.').map(Number);
      const it = this.draft?.checklist?.[i];
      return it && Array.isArray(it.sub) && it.sub[j] ? { it, j } : null;
    };
    body.querySelectorAll<HTMLButtonElement>('[data-cks-toggle]').forEach(btn => btn.addEventListener('click', () => {
      const h = subAt(btn.dataset.cksToggle!); if (!h) return;
      h.it.sub[h.j].done = !h.it.sub[h.j].done;
      this.draw();
      void this.saveDraft(true);
    }));
    body.querySelectorAll<HTMLButtonElement>('[data-cks-del]').forEach(btn => btn.addEventListener('click', () => {
      const h = subAt(btn.dataset.cksDel!); if (!h) return;
      h.it.sub.splice(h.j, 1);
      this.draw();
      void this.saveDraft(true);
    }));

    // comments — photos are STATE-backed (this.commentFiles): the panel redraws
    // on every checklist edit, which would silently wipe a bare file input.
    const cTxt = body.querySelector<HTMLTextAreaElement>('#tk-d-comment');
    cTxt?.addEventListener('input', () => { this.commentText = cTxt.value; });
    // R115 C1: picked people ride along as mentions[] (a duplicated full name inserts the email, so the text resolves too).
    this.bindMentionPicker(cTxt, v => { this.commentText = v; }, e => { if (!this.commentMentions.includes(e)) this.commentMentions.push(e); });
    const cFile = body.querySelector<HTMLInputElement>('#tk-d-cfile');
    cFile?.addEventListener('change', () => { this.commentFiles = Array.from(cFile.files || []); this.draw(); });
    body.querySelector('#tk-d-cfile-clear')?.addEventListener('click', () => { this.commentFiles = []; this.draw(); });
    body.querySelector('#tk-d-send')?.addEventListener('click', () => void this.sendComment());
  }

  private bindCreate(body: HTMLElement): void {
    const backdrop = body.querySelector<HTMLElement>('#tk-create-backdrop');
    backdrop?.addEventListener('click', (e) => { if (e.target === backdrop) this.closeCreate(); });
    body.querySelector('#tk-c-cancel')?.addEventListener('click', () => this.closeCreate());
    body.querySelector('#tk-c-cancel2')?.addEventListener('click', () => this.closeCreate());
    body.querySelectorAll<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>('[data-cfield]').forEach(el => {
      const f = el.dataset.cfield!;
      const apply = () => {
        if (!this.cDraft) return;
        const v: string = (el as any).value;
        if (f === 'assignee' || f === 'due_date' || f === 'due_time') {   // R116 #7: + due_time
          this.cDraft[f] = v || null;
        } else {
          this.cDraft[f] = v;
        }
        if (f === 'priority') this.syncSeg(body, 'data-cpriority', v);
      };
      el.addEventListener('input', apply);
      el.addEventListener('change', apply);
    });
    this.bindSeg(body, 'data-cstatus', () => this.cDraft, 'status', '');
    this.bindSeg(body, 'data-cpriority', () => this.cDraft, 'priority', '[data-cfield="priority"]');
    this.bindListingTypeahead(body, 'data-clisting', () => this.cDraft, v => { this.cListingTyped = v; }, b => { this.cListingBad = b; });   // R104 #7
    this.bindAssigneePicker(body.querySelector('[data-cassignees]'), () => this.cDraft, () => this.cAsQuery, v => { this.cAsQuery = v; });
    this.bindMentionPicker(body.querySelector<HTMLTextAreaElement>('[data-cfield="description"]'), v => { if (this.cDraft) this.cDraft.description = v; });   // R115 C1
    body.querySelector('#tk-c-save')?.addEventListener('click', () => void this.createTask());
    body.querySelector<HTMLInputElement>('[data-cfield="title"]')?.focus();
  }

  // R115 C1: '@' autocomplete on a textarea. The '@token' before the caret
  // (lib/taskMentions mentionQuery — never an email's '@') filters the roster;
  // ↑/↓ + Enter/Tab or a tap inserts '@Full Name ' (or the email when that full
  // name is shared by two logins) at the caret. The menu lives in the
  // textarea's .tk-mwrap (below it, or above for the comment composer) and is
  // never part of draw() — a redraw simply closes it.
  private bindMentionPicker(ta: HTMLTextAreaElement | null, onText: (v: string) => void, onPick?: (email: string) => void): void {
    if (!ta || !ta.parentElement) return;
    const wrap = ta.parentElement;
    let menu: HTMLElement | null = null;
    let rows: any[] = [];
    let sel = 0;
    let q: { start: number; query: string } | null = null;
    const close = () => { menu?.remove(); menu = null; rows = []; q = null; };
    const pick = (p: any) => {
      if (!q || !p) return;
      const caret = ta.selectionStart ?? ta.value.length;
      const r = insertMention(ta.value, q.start, caret, mentionLabel(p, this.mentionPeople));
      ta.value = r.text;
      ta.focus();
      ta.setSelectionRange(r.caret, r.caret);
      onText(r.text);
      if (onPick && p.email) onPick(String(p.email).toLowerCase());
      close();
    };
    const paint = () => {
      if (!q) { close(); return; }
      rows = filterPeople(this.mentionPeople, q.query, 6);
      if (!rows.length) { close(); return; }
      if (sel >= rows.length) sel = 0;
      if (!menu) {
        menu = document.createElement('div');
        menu.className = 'tk-as-menu tk-mmenu';
        menu.setAttribute('role', 'listbox');
        menu.addEventListener('mousedown', (e) => e.preventDefault());   // a pick never blurs the textarea
        menu.addEventListener('click', (e) => {
          const b = (e.target as HTMLElement).closest<HTMLElement>('[data-mention-pick]');
          if (b) pick(rows[Number(b.dataset.mentionPick)]);
        });
        wrap.appendChild(menu);
      }
      menu.innerHTML = `<div class="tk-mmenu-h">Mention — they're notified in the app</div>` + rows.map((p, i) =>
        `<button type="button" class="tk-assignee-chip ${i === sel ? 'active' : ''}" data-mention-pick="${i}" role="option" aria-selected="${i === sel}">${this.avatar(String(p.email))}<span class="tk-as-mname">${this.esc(p.full_name || p.email)}</span><span class="tk-as-role">${this.esc(String(p.role || '').replace(/_/g, ' '))}</span></button>`).join('');
    };
    const update = () => {
      const next = mentionQuery(ta.value, ta.selectionStart ?? ta.value.length);
      if (!next) { close(); return; }
      if (!q || q.start !== next.start || q.query !== next.query) sel = 0;
      q = next;
      paint();
    };
    ta.addEventListener('input', update);
    ta.addEventListener('click', update);
    ta.addEventListener('keydown', (e) => {
      if (!menu || !rows.length) return;
      const k = (e as KeyboardEvent).key;
      if (k === 'ArrowDown') { e.preventDefault(); sel = (sel + 1) % rows.length; paint(); }
      else if (k === 'ArrowUp') { e.preventDefault(); sel = (sel - 1 + rows.length) % rows.length; paint(); }
      else if (k === 'Enter' || k === 'Tab') { e.preventDefault(); pick(rows[sel]); }
      else if (k === 'Escape') { e.preventDefault(); e.stopPropagation(); close(); }
    });
    ta.addEventListener('blur', () => window.setTimeout(() => { if (document.activeElement !== ta) close(); }, 150));
  }

  // R115 C2: one Activity timeline entry — who (⚙ for the reservation sync),
  // when, and a verb line per action. New entries carry field/from/to; legacy
  // ones (only `note`) still read as before.
  // R120: an automatic line names its source (MTR tour sync, PDS flag, Issues
  // cleanup); a `by` that is not a login (a typed name from an old PDS flag)
  // reads "Auto" with that name in the tooltip — never as if that person did it.
  private logItemHtml(l: any): string {
    const by = String(l.by || '');
    const sys = by === 'system';
    const typed = !!by && !sys && !by.includes('@');
    const who = sys ? (SYSTEM_WHO[String(l.source || '')] || 'Reservation sync') : typed ? 'Auto' : (by ? (this.personName(by) || by) : 'Someone');
    const av = sys || typed ? `<span class="tk-av tk-av-sys" title="${typed ? `Automatic — recorded as “${this.esc(by)}”` : 'Automatic'}">⚙</span>` : (by ? this.avatar(by) : '<span class="tk-av none">—</span>');
    return `<li class="tk-tl-item">${av}<div class="tk-tl-b"><div class="tk-tl-h"><b${typed ? ` title="Recorded as “${this.esc(by)}”"` : ''}>${this.esc(who)}</b><span>${this.esc(this.fmtAt(l.at))}</span></div><div class="tk-tl-t">${this.logVerbHtml(l)}</div></div></li>`;
  }
  // R120: plain-word labels ("Due date Oct 19 → Oct 20", "Priority Normal →
  // High", "Assigned Kai · removed Lacey", "Description edited" + Show change,
  // the checklist items by name, "Created the task · due Oct 24 · High ·
  // assigned Kai"). A note-only legacy entry still reads as its note. Any
  // `reason` the server recorded shows muted after the change.
  private logVerbHtml(l: any): string {
    const a = String(l.action || '');
    const note = String(l.note || '');
    const chg = (html: string) => `<span class="tk-tl-chg">${html}</span>`;
    const muted = (t: string) => (t ? ` <span class="tk-tl-muted">${this.esc(t)}</span>` : '');
    const has = l.from !== undefined || l.to !== undefined;
    const isSys = String(l.by || '') === 'system';
    const reason = String(l.reason || '');
    const names = (list: any[]) => {
      const arr = (Array.isArray(list) ? list : []).map(x => this.personName(String(x)) || String(x));
      return arr.length > 3 ? `${arr.slice(0, 3).join(', ')} +${arr.length - 3}` : arr.join(', ');
    };
    const peopleDiff = (from: any, to: any) => {
      const f: string[] = Array.isArray(from) ? from : [], t: string[] = Array.isArray(to) ? to : [];
      const fl = new Set(f.map(x => String(x).toLowerCase())), tl = new Set(t.map(x => String(x).toLowerCase()));
      return { add: t.filter(x => !fl.has(String(x).toLowerCase())), rm: f.filter(x => !tl.has(String(x).toLowerCase())) };
    };
    const dayOr = (d: any) => (d ? this.fmtDate(String(d)) : 'none');
    const timeOr = (v: any) => (/^\d{2}:\d{2}/.test(String(v || '')) ? this.fmtTime(String(v)) : '—');
    const q = (v: any, n = 60) => { const t = String(v == null ? '' : v); return `“${this.esc(t.length > n ? t.slice(0, n - 1) + '…' : t)}”`; };
    let html: string;
    let reasonShown = false;
    switch (a) {
      case 'created': {
        const i = l.init && typeof l.init === 'object' ? l.init : null;
        const bits: string[] = [];
        if (i) {
          if (i.due_date) bits.push(`due ${this.esc(this.fmtDate(i.due_date))}${i.due_time ? ` ${this.esc(timeOr(i.due_time))}` : ''}`);
          if (i.priority && i.priority !== 'normal') bits.push(this.esc(this.priorityLabel(i.priority)));
          if (Array.isArray(i.assignees) && i.assignees.length) bits.push(`assigned ${this.esc(names(i.assignees))}`);
          if (i.status && i.status !== 'todo') bits.push(this.esc(this.statusLabel(i.status)));
          if (Number(i.checklist_n) > 0) bits.push(`${Number(i.checklist_n)} checklist item${Number(i.checklist_n) === 1 ? '' : 's'}`);
        }
        html = `Created the task${bits.length ? ` · ${chg(bits.join(' · '))}` : ''}${muted(note)}`;
        break;
      }
      case 'status': {
        // R115 fix (tasks 3 / cross 3): a note-only entry with no arrow (the
        // onboarding PDS-flag close wrote 'auto · PDS flag cleared' before it
        // logged from/to) reads as its note — never as a "from" status with a
        // dangling arrow. `reason` (onboarding's from/to entries) shows muted.
        if (!has && !note.includes('→')) { html = `Status${muted(note.replace(/^auto · /, ''))}`; break; }
        const [f, t] = has ? [l.from, l.to] : note.split('→');
        html = `Status ${chg(`${this.esc(this.statusLabel(String(f || '').trim()))} → ${this.esc(this.statusLabel(String(t || '').trim()))}`)}${muted(reason)}`;
        reasonShown = true;
        break;
      }
      case 'priority': html = has ? `Priority ${chg(`${this.esc(this.priorityLabel(l.from))} → ${this.esc(this.priorityLabel(l.to))}`)}` : `Priority ${chg(this.esc(note))}`; break;
      case 'due': {
        if (has) {
          html = `${isSys ? 'Moved the due date' : 'Due date'} ${chg(`${this.esc(dayOr(l.from))} → ${this.esc(dayOr(l.to))}`)}${isSys ? muted(reason || 'the reservation’s dates changed') : ''}`;
          reasonShown = isSys;
        } else html = isSys ? this.esc(note.replace(/^auto · /, '')) : `Due date ${chg(this.esc(note))}`;
        break;
      }
      case 'assignees': case 'watchers': {
        if (!has) { html = `${a === 'assignees' ? 'Assignees' : 'Watchers'} ${chg(this.esc(note))}`; break; }
        const { add, rm } = peopleDiff(l.from, l.to);
        const parts: string[] = [];
        if (a === 'assignees') {
          if (add.length) parts.push(`Assigned ${chg(this.esc(names(add)))}`);
          if (rm.length) parts.push(`${parts.length ? 'removed' : 'Removed'} ${chg(this.esc(names(rm)))}`);
        } else {
          if (add.length) parts.push(`Watching: added ${chg(this.esc(names(add)))}`);
          if (rm.length) parts.push(`${parts.length ? 'removed' : 'Watching: removed'} ${chg(this.esc(names(rm)))}`);
        }
        html = parts.join(' · ') || `${a === 'assignees' ? 'Assignees' : 'Watchers'} ${chg(this.esc(note))}`;
        break;
      }
      case 'due_time': html = has ? `Due time ${chg(`${this.esc(timeOr(l.from))} → ${this.esc(timeOr(l.to))}`)}` : `Due time ${chg(this.esc(note))}`; break;
      case 'title': html = has && typeof l.from === 'string' && typeof l.to === 'string' ? `Title ${chg(`${q(l.from)} → ${q(l.to)}`)}` : `Renamed ${chg(this.esc(note))}`; break;
      case 'description': {
        const ft = l.from_text != null ? String(l.from_text) : null, tt = l.to_text != null ? String(l.to_text) : null;
        html = 'Description edited';
        if (ft != null || tt != null) {
          html += ` <details class="tk-tl-diff"><summary>Show change</summary>`
            + `<div class="tk-tl-was"><span class="tk-tl-lab">Before</span>${ft ? this.esc(ft) : '<i>empty</i>'}</div>`
            + `<div class="tk-tl-now"><span class="tk-tl-lab">After</span>${tt ? this.esc(tt) : '<i>empty</i>'}</div></details>`;
        }
        break;
      }
      case 'checklist': {
        const lists = ['checked', 'unchecked', 'added', 'removed', 'renamed', 'edited'];
        if (!lists.some(k => Array.isArray(l[k]) && l[k].length)) { html = this.esc(note); break; }   // legacy: the note as written
        const items: string[] = [];
        const each = (k: string, sign: string) => (Array.isArray(l[k]) ? l[k] : []).forEach((x: any) => items.push(`${sign} ${q(x, 48)}`));
        each('checked', '✓'); each('unchecked', '↺'); each('added', '+'); each('removed', '−');
        (Array.isArray(l.renamed) ? l.renamed : []).forEach((r: any) => items.push(`renamed ${q(r && r.from, 40)} → ${q(r && r.to, 40)}`));
        each('edited', 'edited');
        const prog = has && l.from !== l.to ? ` ${chg(`${this.esc(l.from)} → ${this.esc(l.to)}`)}` : '';
        html = `Checklist${prog} <span class="tk-tl-items">${items.join(' · ')}</span>`;
        break;
      }
      case 'listing': html = `Property ${chg(this.esc(note))}`; break;
      case 'link': html = `Linked ${chg(this.esc(note))}`; break;
      case 'blocked_by': html = `Blocked by ${chg(this.esc(note))}`; break;
      case 'recurrence': html = `Repeats ${chg(this.esc(note))}`; break;
      case 'template_id': html = `Template ${chg(this.esc(note))}`; break;
      case 'comment': {
        const m: string[] = Array.isArray(l.mentions) ? l.mentions : [];
        const base = note.split(' · ')[0] || 'commented';
        html = `${this.esc(base)}${m.length ? ` · ${m.map(e => `<span class="tk-mention">@${this.esc(this.personName(e) || e)}</span>`).join(' ')}` : ''}`;
        break;
      }
      case 'note': html = this.esc(isSys ? note.replace(/^auto · /, '') : note); reasonShown = true; break;
      case 'tour': html = `🗓 ${this.esc(note)}`; break;   // R116 #7: the MTR tour's own lines (dates differ / match)
      default: html = `${this.esc(a.replace(/_/g, ' '))}${note ? ` · ${this.esc(note)}` : ''}`;
    }
    return reasonShown ? html : `${html}${muted(reason)}`;
  }

  // Segmented control ↔ select sync (both write the same draft field; neither redraws).
  private bindSeg(scope: HTMLElement, attr: string, getDraft: () => any, field: string, selSelector: string): void {
    scope.querySelectorAll<HTMLButtonElement>(`[${attr}]`).forEach(btn => btn.addEventListener('click', () => {
      const draft = getDraft(); if (!draft) return;
      const v = btn.getAttribute(attr) || '';
      draft[field] = v;
      this.syncSeg(scope, attr, v);
      if (selSelector) { const sel = scope.querySelector<HTMLSelectElement>(selSelector); if (sel) sel.value = v; }
    }));
  }
  private syncSeg(scope: HTMLElement, attr: string, v: string): void {
    scope.querySelectorAll<HTMLButtonElement>(`[${attr}]`).forEach(b => b.classList.toggle('active', b.getAttribute(attr) === v));
  }

  // Multi-assignee picker (R18 #12 semantics kept) — mutate draft.assignees in
  // place + keep draft.assignee (the mirror) pointed at the first. Only the
  // picker box re-renders (innerHTML), so the search box keeps focus/caret.
  // R115: field 'watchers' drives the Details card's watchers editor with the same picker.
  private bindAssigneePicker(scope: HTMLElement | null, getDraft: () => any, getQ: () => string, setQ: (v: string) => void, field: 'assignees' | 'watchers' = 'assignees'): void {
    if (!scope) return;
    const selBox = scope.querySelector<HTMLElement>('.tk-as-sel');
    const menu = scope.querySelector<HTMLElement>('.tk-as-menu');
    const inp = scope.querySelector<HTMLInputElement>('[data-assignee-search]');
    if (!selBox || !menu || !inp) return;
    let open = false;   // focus-scoped; a full draw() shows the menu only while a query is typed
    const current = (draft: any) => (field === 'watchers' ? this.taskWatchers(draft) : this.taskAssignees(draft));
    const toggle = (email: string) => {
      const draft = getDraft(); if (!draft) return;
      const cur = new Set(current(draft));
      if (cur.has(email)) cur.delete(email); else cur.add(email);
      const arr = [...cur];
      if (field === 'watchers') { draft.watchers = arr; return; }
      draft.assignees = arr;
      draft.assignee = arr[0] || null;
    };
    // Only the chips and the menu re-render; the input (and its focus/caret) is untouched.
    const rerender = () => {
      const draft = getDraft(); if (!draft) return;
      const selected = current(draft);
      selBox.innerHTML = this.assigneeSelHtml(selected, field === 'watchers' ? 'No watchers' : '');
      menu.innerHTML = this.assigneeMenuHtml(selected, getQ());
      menu.hidden = !(open || getQ().trim());
      wireRows();
    };
    const wireRows = () => {
      menu.querySelectorAll<HTMLElement>('[data-assignee-toggle]').forEach(btn => btn.addEventListener('click', () => {
        toggle(btn.dataset.assigneeToggle!);
        btn.classList.toggle('active');   // the shared .chip/.tk-assignee-chip active spelling
        rerender();
      }));
      selBox.querySelectorAll<HTMLElement>('[data-assignee-remove]').forEach(btn => btn.addEventListener('click', () => {
        toggle(btn.dataset.assigneeRemove!);
        rerender();
      }));
    };
    inp.addEventListener('input', () => { setQ(inp.value); open = true; rerender(); });
    inp.addEventListener('focus', () => { open = true; rerender(); });
    inp.addEventListener('keydown', (e) => {
      const k = (e as KeyboardEvent).key;
      if (k === 'Escape') { e.preventDefault(); setQ(''); inp.value = ''; inp.blur(); }   // blur closes the menu
      if (k === 'Enter') {   // first match toggles — fast keyboard add
        e.preventDefault();
        const first = menu.querySelector<HTMLElement>('[data-assignee-toggle]');
        if (first) { toggle(first.dataset.assigneeToggle!); setQ(''); inp.value = ''; rerender(); }
      }
    });
    // Blur closes the menu unless focus moved inside the picker; mousedown on the
    // menu / chips is prevented so a pick never blurs the input in the first place.
    inp.addEventListener('blur', (e) => {
      const to = (e as FocusEvent).relatedTarget as Node | null;
      if (to && scope.contains(to)) return;
      open = false; rerender();
    });
    menu.addEventListener('mousedown', (e) => e.preventDefault());
    selBox.addEventListener('mousedown', (e) => { if ((e.target as HTMLElement).closest('button')) e.preventDefault(); });
    wireRows();
  }

  // ── actions ───────────────────────────────────────────────────
  private openDrawer(id: string): void {
    const t = this.taskById(id);
    if (!t) return;
    this.drawerId = id;
    this.drawerEpoch++;                         // R115 fix 2: saves from an earlier opening no longer own the draft
    this.draft = this.seedDraft(t);
    this.draftBase = this.seedDraft(t);
    this.panelLanded = null;
    this.ckText = '';
    this.commentText = '';
    this.commentFiles = [];                     // R115 fix 2: picked photos belong to the panel they were picked in
    this.commentMentions = []; this.wsQuery = ''; this.logAll = false;   // R115
    this.logClosed = false; this.closeAsk = false;   // R120: every task opens with its Activity shown (the collapse was sticking across tasks)
    this.ckOpen.clear(); this.ckSubText = {};   // R104 #8
    this.asQuery = '';
    this.seedListingTyped();                    // R104 #7
    this.draw();
  }
  // R104 #7: what the panel's property box shows for the current draft (keeps
  // a name on a task whose listing went inactive).
  private seedListingTyped(): void {
    const l = this.listings.find(x => String(x.id) === String(this.draft?.listing_id || ''));
    this.listingTyped = l ? this.listingLabelFor(l) : String(this.draft?.listing_name || '');
    this.listingBad = false;
  }
  private closeDrawer(): void {
    this.drawerId = null;
    this.drawerEpoch++;   // R115 fix 2: a queued / in-flight save still lands (runSave) — it just no longer owns a draft
    this.draft = null;
    this.draftBase = null;
    this.panelLanded = null;
    this.ckText = '';
    this.commentText = '';
    this.commentFiles = [];
    this.commentMentions = []; this.wsQuery = ''; this.logAll = false;   // R115
    this.closeAsk = false;                      // R120
    this.ckOpen.clear(); this.ckSubText = {};
    this.listingTyped = ''; this.listingBad = false;
    this.asQuery = '';
    this.draw();
  }
  // R120: ✕ / backdrop — close at once when nothing is unsaved, else ask.
  private requestClose(): void {
    if (!this.unsavedKeys().length) { this.closeDrawer(); return; }
    this.closeAsk = true;
    this.draw();
  }
  // R120: the draft keys the server doesn't have yet. changedKeys(draft,
  // draftBase) alone over-reports: the base is not re-based while a silent
  // checklist save is pending (foldAnswer waits for the queue), so a tick that
  // is on its way — or already landed — would read as unsaved. A key counts
  // only when the draft's value is in NONE of: the panel's base, the newest
  // server copy, the list row, the capture of a save still queued from this
  // opening, or the newest save of it that landed.
  private unsavedKeys(): string[] {
    const d = this.draft;
    if (!d || !d.id || !this.draftBase) return [];
    const id = String(d.id);
    const J = (v: any) => JSON.stringify(v === undefined ? null : v);
    const refs: any[] = [this.draftBase, this.serverCopy.get(id), this.taskById(id), this.panelLanded].filter(Boolean);
    const pend = this.lastJob.get(id);
    if (pend && !pend.done && pend.epoch === this.drawerEpoch) refs.push(pend.want0);
    return changedKeys(d, this.draftBase).filter(k => refs.every(r => J(d[k]) !== J(r[k])));
  }
  private closeCreate(): void {
    this.showCreate = false;
    this.cDraft = null;
    this.cListingTyped = ''; this.cListingBad = false;
    this.cAsQuery = '';
    this.draw();
  }

  // Persist the panel draft. silent=true skips the "Saved." note (checklist taps).
  // R115 fix (review tasks 0): one drawer save at a time (a fast second tick
  // diffs against the first one's echo), and each sends only what changed.
  // R115 fix 2 (verifier D0): the save is captured HERE, when it is queued —
  // and runSave works only from that capture, never from this.draft. So a
  // save queued behind another one, or still in flight, lands even when the
  // panel closes or another task opens; a 409 is replayed on + retried
  // whether or not the panel is still open; and anything that can't land is
  // alerted BY TASK NAME — never dropped silently.
  // R115 fix 3 (verifier D0 regression — a queued save sent another user's
  // older value back): three rules, together provable (lib/taskPatch.mjs,
  // scripts/test_tasks_fixes_dom.mjs V1–V9 + FUZZ):
  //  1. Every capture of one panel opening is on ONE layer: the panel's base
  //     (draftBase) is never re-based while that opening has a save pending —
  //     409 copies, echoes and status / comment answers are only adopted
  //     (serverCopy + the row) until the queue drains (rebasePanel). So
  //     want0 (the draft now) vs base0 (the previous capture, or the base)
  //     is exactly this save's own edits: an undo is an edit, and a value
  //     another user set is never one.
  //  2. A save is REPLAYED onto the newest server copy (replaySave): first
  //     the edits of the saves ahead of it that did not land (it carries
  //     them — nobody was told about those), then its own. A save that
  //     landed is in that copy already and is never replayed.
  //  3. When the queue drains, the panel is re-based once, against the
  //     newest landed capture (what this panel actually put on the server) —
  //     never against a copy older than the user's own undo.
  private saveDraft(silent = false): Promise<void> {
    const d = this.draft;
    if (!d || !d.id) return Promise.resolve();
    const id = String(d.id);
    const titleBad = !String(d.title || '').trim();
    if (!silent && (titleBad || this.listingBad)) {   // the explicit Save: say what's wrong, send nothing
      alert(titleBad ? 'Title is required.' : 'Property doesn’t match any listing — pick one from the list or clear the field.');   // R104 #7
      this.draw();   // review #21: the DOM must follow the mutated draft
      return Promise.resolve();
    }
    const prev = this.lastJob.get(id);
    const dep = prev && !prev.done && prev.epoch === this.drawerEpoch ? prev : null;
    const layer = this.seedDraft(this.draftBase || d);
    // no save of this opening pending → the queue drained and the panel was
    // re-based (rebasePanel): its base is what the server has from it
    const base0 = this.seedDraft(dep ? dep.want0 : layer);
    const want0 = this.cloneTask(d);
    // A checklist tap still lands while the title box is blank / the property
    // box holds a non-match: that field is left out of it (it stays in the
    // panel, unsaved, for the explicit Save to flag).
    if (titleBad) want0.title = base0.title;
    if (this.listingBad) { want0.listing_id = base0.listing_id; want0.listing_name = base0.listing_name; }
    const job: SaveJob = { id, want0, base0, layer, epoch: this.drawerEpoch, silent, dep, landed: false, done: false };
    this.lastJob.set(id, job);
    const run = this.saveQ.then(() => this.runSave(job));
    this.saveQ = run.catch(() => { /* runSave alerts its own failures */ });
    return run;
  }
  private async runSave(job: SaveJob): Promise<void> {
    try {
      await this.sendSave(job);
    } finally {
      job.done = true;
      if (job.landed) job.dep = null;   // landed → in every newer server copy; nothing behind it replays past it
      if (this.lastJob.get(job.id) === job) {   // this task's queue is through
        this.lastJob.delete(job.id);
        this.rebasePanel(job);
      }
    }
  }
  // R115 fix 3: the one point the open panel of this task is re-based on the
  // newest server copy — when its save queue has drained. Against what this
  // opening's saves put on the server (the capture of the newest one that
  // landed; the panel's base when none did — a failed change stays in the
  // panel for the next Save), so a tick → untick never reads as "untouched".
  private rebasePanel(job: SaveJob): void {
    const landed = this.panelLanded;
    this.panelLanded = null;
    if (!this.draft || this.draft.id !== job.id || !this.draftBase) return;
    const newest = this.serverCopy.get(job.id);
    if (!newest || this.olderThanPanel(newest)) return;
    const against = this.seedDraft(landed || this.draftBase);
    if (JSON.stringify(this.draft) === JSON.stringify(against)) {
      // nothing typed / failed since: adopt the server's copy whole (review R104 #20)
      this.draft = this.seedDraft(newest);
      this.draftBase = this.seedDraft(newest);
      this.seedListingTyped();
      this.draw();
    } else {
      this.foldIntoDraft(newest, against);
      // a silent tick with typing since: the DOM already shows the newer local draft
      if (!(job.silent && job.epoch === this.drawerEpoch)) this.draw();
    }
  }
  // The newest copy of this task we know: any server answer's (serverCopy), or
  // the copy the panel had when this save was queued (a reload can be newer
  // than every answer).
  private newestCopy(job: SaveJob): any {
    const known = this.serverCopy.get(job.id);
    return known && String(known.updated_at || '') > String(job.layer.updated_at || '') ? known : job.layer;
  }
  private async sendSave(job: SaveJob): Promise<void> {
    if (this.goneIds.has(job.id)) return;   // deleted (here, or a 404 the user was already told about)
    const label = `“${String(job.want0.title || job.base0.title || 'this task').slice(0, 60)}”`;
    // the panel this save came from is still the open one
    const inPanel = () => !!this.draft && this.drawerId === job.id && this.drawerEpoch === job.epoch;
    // A failure is alerted by task name — unless a save queued behind this one
    // (chained: same task, same panel opening) carries this change too; then
    // that one resends it, and alerts if IT can't land.
    const fail = (msg: string) => {
      const next = this.lastJob.get(job.id);
      if (!(next && next !== job && !next.done && next.epoch === job.epoch)) alert(msg);
      this.draw();
    };
    for (let attempt = 0; ; attempt++) {
      // Replay onto the newest copy any answer has carried (an earlier save's
      // echo, a status / comment answer, a 409): keys no queued save changed
      // take that copy's value, so they're not sent.
      const server = this.newestCopy(job);
      const want = this.seedDraft(replaySave(job, server));
      const u = this.unsure.get(job.id);
      const unsure = !!u && String(server.updated_at || '') <= u.at;
      const body = patchBody(want, server, unsure ? u!.keys : [], unsure);
      if (!body) {                                 // nothing changed since the last echo — nothing to send
        this.landedSave(job);
        if (!job.silent) { this.note = 'Saved.'; this.draw(); }
        return;
      }
      let r: { ok: boolean; status: number; j: any };
      try {
        r = await this.postRaw('', body);
      } catch {
        this.markUnsure(job.id, body, server);
        // panel open: the draft still holds the change and the next Save resends it
        fail(`Couldn’t save ${label} — check the connection and try again.${inPanel() ? '' : ' Open the task to redo the change.'}`);
        return;
      }
      const fresh = r.j && r.j.task && String(r.j.task.id) === job.id ? r.j.task : null;
      // Any copy an answer carries becomes the newest copy (serverCopy + the
      // row); an open panel of this task takes it when its own queue drains.
      if (fresh) { this.adoptServerCopy(fresh); this.foldAnswer(fresh); }
      if (r.status === 409 && fresh) {
        // someone else saved this task since that copy: replay this save on
        // top of theirs — once, open panel or not.
        if (attempt === 0) continue;
        fail(`Couldn’t save ${label} — someone else is saving it right now. ${inPanel() ? 'Check it and save again.' : 'Open it, check it and save again.'}`);
        return;
      }
      if (!r.ok) {
        if (r.status === 404 && (this.goneIds.has(job.id) || this.deleting.has(job.id))) {   // deleted here (or being deleted) while it flew, or already reported
          this.goneIds.add(job.id);
          return;
        }
        if (r.status === 404) {                    // deleted meanwhile — never re-created; its queued saves stop
          this.goneIds.add(job.id);
          alert(`Couldn’t save ${label}: ${r.j.error || 'task not found — it may have been deleted'}`);
          this.dropTask(job.id);
          return;
        }
        if (r.status >= 500) this.markUnsure(job.id, body, server);   // it may have been written before the error
        fail(`Couldn’t save ${label}: ${r.j.error || `request failed (${r.status})`}`);
        return;
      }
      this.landedSave(job);
      if (!job.silent) this.note = 'Saved.';
      // review R104 #20: a silent tick while the user typed / ticked again
      // since — the DOM already shows the newer local draft; no redraw
      if (!(job.silent && inPanel() && JSON.stringify(this.draft) !== JSON.stringify(job.want0))) this.draw();
      return;
    }
  }
  // R115 fix 3: this body's outcome is unknown — see `unsure`.
  private markUnsure(id: string, body: any, server: any): void {
    const at = String((server && server.updated_at) || '');
    const prev = this.unsure.get(id);
    const keys = new Set(prev && prev.at === at ? prev.keys : []);
    for (const k of Object.keys(body || {})) if (k !== 'id' && k !== 'base_updated_at') keys.add(k);
    this.unsure.set(id, { keys: [...keys], at });
  }
  // A save the server took (or one with nothing left to send). For the open
  // panel it came from, its capture is what that panel has put on the server
  // so far (rebasePanel folds against it).
  private landedSave(job: SaveJob): void {
    job.landed = true;
    if (this.draft && this.drawerId === job.id && this.drawerEpoch === job.epoch) this.panelLanded = job.want0;
  }
  // A task deleted by someone else while its panel was open (POST → 404).
  private dropTask(id: string): void {
    this.tasks = (this.tasks || []).filter(x => x.id !== id);
    if (this.drawerId === id) this.closeDrawer(); else this.draw();
  }

  // R115 fix (review tasks 0): the row select / My Work "done" send ONLY the
  // status — the list copy may be minutes old, and re-sending it whole
  // reverted (and logged + notified) other people's newer edits.
  private async postStatus(id: string, status: string): Promise<void> {
    const t = this.taskById(id);
    if (!t) return;
    const j = await this.post('', { id, status });
    if (j && j.task) {
      this.adoptServerCopy(j.task);   // R115 fix 2: keyed by the answer's id (a reload may have replaced `t`)
      if (this.drawerId === id) this.foldAnswer(j.task);
    }
    this.draw();
  }

  // R112: board drop — move the card now, POST the new status, revert if the
  // server says no. R115 fix: the body is `{ id, status }` only (never the
  // stale list copy); the server stamps completed_at itself.
  private async moveTask(id: string, status: string): Promise<void> {
    const t = this.taskById(id);
    if (!t || t.status === status || !STATUSES.some(([k]) => k === status)) return;
    const payload = { id, status };
    const prev = { status: t.status, completed_at: t.completed_at };
    t.status = status;
    if (status === 'done' && !t.completed_at) t.completed_at = new Date().toISOString();   // placement only (Done column sorts by it); the echo overwrites
    this.draw();
    const j = await this.post('', payload);
    if (j && j.task) {
      this.adoptServerCopy(j.task);
      if (this.drawerId === id) this.foldAnswer(j.task);
    } else {
      t.status = prev.status; t.completed_at = prev.completed_at;
    }
    this.draw();
  }

  private async addChecklistItem(): Promise<void> {
    const title = this.ckText.trim();
    if (!title || !this.draft) return;
    if (!Array.isArray(this.draft.checklist)) this.draft.checklist = [];
    this.draft.checklist.push({ key: `ck_${Date.now().toString(36)}`, title, done: false, note: '', description: '', instructions: '', sub: [] });
    this.ckText = '';
    await this.saveDraft(true);
  }

  // R104 #8: a sub-item under checklist item i (the item stays expanded so you can keep adding).
  private async addSubItem(i: number): Promise<void> {
    const it = this.draft?.checklist?.[i]; if (!it) return;
    const key = String(it.key || i);
    const title = String(this.ckSubText[key] || '').trim();
    if (!title) return;
    if (!Array.isArray(it.sub)) it.sub = [];
    it.sub.push({ key: `cks_${Date.now().toString(36)}`, title, done: false });
    this.ckSubText[key] = '';
    this.ckOpen.add(key);
    await this.saveDraft(true);
  }

  private async sendComment(): Promise<void> {
    // R115 fix 2: the task + panel opening are captured NOW — a photo upload
    // or the POST can outlast the panel (closed, or another task opened), and
    // the comment must still go to (and its answer land on) THIS task.
    const id = this.drawerId;
    if (!id) return;
    const epoch = this.drawerEpoch;
    const files = this.commentFiles;         // state, not DOM — survives redraws
    let text = this.commentText.trim();
    if (!text && !files.length) return;
    if (!text) text = files.length > 1 ? `📷 ${files.length} photos` : '📷 Photo'; // server requires text
    let photos: string[] = [];
    if (files.length) {
      photos = await this.uploadPhotos(files);
      if (!photos.length) return;            // total failure — keep the draft so the user can retry
    }
    // R115 C1: the picker's picks still named in the text (the server also parses @Names itself).
    const mentions = keepPicked(text, this.commentMentions, this.mentionPeople);
    let r: { ok: boolean; status: number; j: any } | null = null;
    try { r = await this.postRaw(`/${encodeURIComponent(id)}/comments`, { text, photos, mentions }); } catch { r = null; }
    if (!r || !r.ok) {
      // R115 fix 3: never silent — name the task; when the composer it came
      // from is gone (another task opened / panel closed), hand the text back.
      const t = this.taskById(id);
      const label = `“${String((t && t.title) || 'this task').slice(0, 60)}”`;
      const why = r ? `: ${r.j.error || `request failed (${r.status})`}` : ' — check the connection and try again.';
      alert(`Couldn’t post your comment on ${label}${why}${this.drawerEpoch === epoch ? '' : `\n\nYour comment (not posted): ${text}`}`);
      this.draw();
      return;
    }
    const j = r.j;
    if (j && j.task) {
      this.adoptServerCopy(j.task);
      this.foldAnswer(j.task);   // R115 fix: the comment moved updated_at — keep the draft's base current (no-op on another task's panel; held while a save is pending)
      if (this.drawerEpoch === epoch) {   // the composer that sent it is still the one on screen
        this.commentText = '';
        this.commentMentions = [];
        this.commentFiles = [];
        this.note = '';
      }
    }
    this.draw();
  }

  // Uploads in chunks of ≤5 files per request (server multer files:5 limit).
  // Returns the URLs that made it up; alerts once if any chunk failed.
  private async uploadPhotos(files: File[]): Promise<string[]> {
    const urls: string[] = [];
    let failed = 0;
    for (let i = 0; i < files.length; i += 5) {
      const fd = new FormData();
      for (const f of files.slice(i, i + 5)) fd.append('photo', f);
      try {
        const r = await fetch(`${API}/photo`, { method: 'POST', body: fd });
        const j = await r.json().catch(() => ({} as any));
        if (!r.ok) { failed += Math.min(5, files.length - i); continue; }
        if (Array.isArray(j.urls)) urls.push(...j.urls);
        else if (j.url) urls.push(j.url);
      } catch { failed += Math.min(5, files.length - i); }
    }
    if (failed) alert(`${failed} photo${failed === 1 ? '' : 's'} failed to upload${urls.length ? ` (${urls.length} succeeded)` : ''}.`);
    return urls;
  }

  // One-tap proof photos from a My Work card: /photo then /:id/comments.
  private async attachProof(id: string, inp: HTMLInputElement): Promise<void> {
    const files = Array.from(inp.files || []);
    if (!files.length) return;
    this.note = files.length > 1 ? `Uploading ${files.length} photos…` : 'Uploading photo…';
    this.draw();
    try {
      const urls = await this.uploadPhotos(files);
      if (!urls.length) { this.note = ''; this.draw(); return; }
      const j = await this.post(`/${encodeURIComponent(id)}/comments`, {
        text: urls.length > 1 ? `📷 ${urls.length} proof photos` : '📷 Proof photo',
        photos: urls,
      });
      if (j && j.task) {
        this.adoptServerCopy(j.task);
        if (this.drawerId === id) this.foldAnswer(j.task);
      }
      this.note = urls.length > 1 ? `${urls.length} photos attached.` : 'Photo attached.';
    } catch (e: any) {
      this.note = `Photo failed: ${e.message}`;
    }
    this.draw();
  }

  private async deleteTask(): Promise<void> {
    const id = this.drawerId;   // R115 fix 2: captured — the panel may switch while the DELETE flies
    if (!id) return;
    const t = this.taskById(id);
    if (!confirm(`Delete "${String((t && t.title) || 'this task').slice(0, 60)}"? This cannot be undone.`)) return;
    let ok = false;
    this.deleting.add(id);      // R115 fix 3: a drawer save that 404s meanwhile is this delete landing first — no second alert
    try { ok = await this.del(`/${encodeURIComponent(id)}`); }
    catch { alert(`Couldn’t delete “${String((t && t.title) || 'this task').slice(0, 60)}” — check the connection and try again.`); }
    finally { this.deleting.delete(id); }
    if (ok) {
      this.goneIds.add(id);     // a save still queued for it stops quietly (deleted on purpose)
      this.tasks = (this.tasks || []).filter(x => x.id !== id);
      this.note = 'Task deleted.';
      if (this.drawerId === id) { this.drawerId = null; this.drawerEpoch++; this.draft = null; this.draftBase = null; this.panelLanded = null; }
    }
    this.draw();
  }

  private async createTask(): Promise<void> {
    if (!this.cDraft) return;
    if (!String(this.cDraft.title || '').trim()) { alert('Title is required.'); return; }
    if (this.cListingBad) { alert('Property doesn’t match any listing — pick one from the list or clear the field.'); return; }   // R104 #7
    const j = await this.post('', this.cDraft);
    if (j && j.task) {
      (this.tasks || []).push(j.task);
      this.showCreate = false;
      this.cDraft = null;
      this.note = 'Task created.';
    }
    this.draw();
  }
}
